import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/placeholders";
import BottoneRaccogli from "@/components/BottoneRaccogli";
import { useDaCerca } from "@/lib/cerca";
import {
  ShieldCheck, CalendarClock, ClipboardList, FileText, Plus, ExternalLink,
  Check, Trash2, Pencil, History, Loader2, Search,
  CheckCircle2, XCircle, Clock, RefreshCw, Shield,
  AlertTriangle, FileSignature, Flag, Scale, Undo2,
  Gauge, Flame, ChevronRight, BookOpen, ArrowLeft,
  FileArchive, Download, FileSpreadsheet, Upload, TrendingUp, ClipboardCheck,
  ShieldQuestion, Network,
} from "lucide-react";
import SchedeOperative from "@/components/SchedeOperative";
import SceltaMezzo from "@/components/SceltaMezzo";
import VisiteEsterne from "@/components/VisiteEsterne";
import Organigramma from "@/components/Organigramma";

// Come si colora uno stato. "Da avviare" non e' rosso: il controllo non e'
// in ritardo, semplicemente non l'ha ancora fatto nessuno.
const STATI = {
  scaduto:         { nome: "Scaduto",     classe: "bg-red-100 text-red-700 border-red-200",       bordo: "border-red-200 bg-red-50/60" },
  in_scadenza:     { nome: "In scadenza", classe: "bg-amber-100 text-amber-800 border-amber-200", bordo: "border-amber-200 bg-amber-50/60" },
  da_avviare:      { nome: "Da avviare",  classe: "bg-sky-100 text-sky-800 border-sky-200",       bordo: "border-sky-200 bg-sky-50/50" },
  in_regola:       { nome: "In regola",   classe: "bg-emerald-100 text-emerald-800 border-emerald-200", bordo: "border-slate-200" },
  non_programmato: { nome: "Non programmato", classe: "bg-slate-100 text-slate-600 border-slate-200", bordo: "border-slate-200" },
};

const TIPI_SCADENZA = {
  controllo:       { nome: "Controllo",       icona: ClipboardList },
  audit:           { nome: "Audit",           icona: ShieldCheck },
  certificato:     { nome: "Certificato",     icona: Shield },
  attestato:       { nome: "Attestato",       icona: FileText },
  manutenzione:    { nome: "Manutenzione",    icona: RefreshCw },
  documento:       { nome: "Documento",       icona: FileText },
  non_conformita:  { nome: "Azione correttiva", icona: AlertTriangle },
  taratura:        { nome: "Taratura",        icona: Gauge },
};

// Come sta una voce nella copertura di un controllo.
const COPERTURA = {
  scoperto: { nome: "Manca",     classe: "bg-red-100 text-red-700 border-red-200",       punto: "#dc2626" },
  anomalia: { nome: "Attenzione", classe: "bg-amber-100 text-amber-800 border-amber-200", punto: "#d97706" },
  coperto:  { nome: "A posto",   classe: "bg-emerald-100 text-emerald-800 border-emerald-200", punto: "#059669" },
};

// Il ciclo di una non conformità, in ordine.
const STATI_NC = {
  aperta:        { nome: "Aperta",         classe: "bg-sky-100 text-sky-800 border-sky-200" },
  in_corso:      { nome: "In lavorazione", classe: "bg-amber-100 text-amber-800 border-amber-200" },
  da_verificare: { nome: "Da verificare",  classe: "bg-violet-100 text-violet-800 border-violet-200" },
  chiusa:        { nome: "Chiusa",         classe: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  annullata:     { nome: "Annullata",      classe: "bg-slate-100 text-slate-500 border-slate-200" },
};

const GRAVITA_UI = {
  alta:  "bg-red-100 text-red-700 border-red-200",
  media: "bg-amber-100 text-amber-800 border-amber-200",
  bassa: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATI_AUDIT_UI = {
  pianificato: { nome: "Pianificato", classe: "bg-sky-100 text-sky-800 border-sky-200" },
  svolto:      { nome: "Svolto",      classe: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  chiuso:      { nome: "Chiuso",      classe: "bg-slate-100 text-slate-600 border-slate-200" },
};

const STATI_OBIETTIVO_UI = {
  aperto:        { nome: "Aperto",        classe: "bg-sky-100 text-sky-800 border-sky-200" },
  raggiunto:     { nome: "Raggiunto",     classe: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  non_raggiunto: { nome: "Non raggiunto", classe: "bg-red-100 text-red-700 border-red-200" },
  annullato:     { nome: "Annullato",     classe: "bg-slate-100 text-slate-500 border-slate-200" },
};

const MESI = ["", "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

const dataIt = (iso) => {
  if (!iso) return "—";
  const [a, m, g] = String(iso).substring(0, 10).split("-");
  return `${g}/${m}/${a}`;
};

const meseAnno = (iso) => {
  if (!iso) return "—";
  const [a, m] = String(iso).substring(0, 10).split("-");
  return `${MESI[Number(m)]} ${a}`;
};

const giorniA = (iso) => {
  if (!iso) return null;
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${String(iso).substring(0, 10)}T00:00:00`) - oggi) / 86400000);
};

function Pallino({ stato }) {
  const s = STATI[stato] || STATI.non_programmato;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.classe}`}>
      {s.nome}
    </span>
  );
}

function Tessere({ sistemi, codici, compatte = false }) {
  if (!codici || codici.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {codici.map((c) => {
        const s = sistemi.find((x) => x.codice === c);
        if (!s) return null;
        return (
          <span key={c}
                className={`inline-flex items-center gap-1 rounded border border-slate-200 bg-white ${compatte ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"} font-medium text-slate-600`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.colore }} />
            {s.nome}
          </span>
        );
      })}
    </div>
  );
}

export default function SistemiIntegrati() {
  const { user } = useAuth();
  const puoScrivere = user?.role === "admin" || user?.role === "amministrativo";

  const [scheda, setScheda] = useState("cruscotto");
  const [caricamento, setCaricamento] = useState(true);
  const [avvio, setAvvio] = useState(false);
  const [cruscotto, setCruscotto] = useState(null);
  const [riferimenti, setRiferimenti] = useState(null);
  const [sistemaSel, setSistemaSel] = useState("");
  // Quale quaderno si sta guardando: sta qui perche' dalle schede di
  // controllo si salta dritti dentro al registro giusto.
  const [registroAperto, setRegistroAperto] = useState(null);
  // Una scheda da aprire subito in compilazione: ci si arriva dal fascicolo
  // del dipendente («Due diligence») o dal documento nel registro, e il
  // modulo deve nascere senza altri clic. `persona` e' chi riguarda: la
  // scheda che lo prevede parte con l'anagrafica dentro.
  const [daCompilare, setDaCompilare] = useState(null);   // {slug, persona}
  // Dal cerca in alto si arriva dritti alla scheda, o dentro un registro.
  useDaCerca("scheda", setScheda);
  useDaCerca("registro", (codice) => { setRegistroAperto(codice); setScheda("registri"); });
  useDaCerca("compila", (slug) => {
    let persona = "";
    try { persona = new URLSearchParams(window.location.search).get("persona") || ""; }
    catch { persona = ""; }
    setDaCompilare({ slug, persona });
    setScheda("schede");
  });

  const sistemi = cruscotto?.sistemi || [];
  const daPreparare = Boolean(cruscotto) && (cruscotto.totali?.controlli || 0) === 0;

  const caricaCruscotto = useCallback(async () => {
    try {
      setCaricamento(true);
      const [c, r] = await Promise.all([
        api.get("/sgi/cruscotto"),
        api.get("/sgi/riferimenti"),
      ]);
      setCruscotto(c.data);
      setRiferimenti(r.data);
    } catch {
      toast.error("Errore nel caricamento della sezione");
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { caricaCruscotto(); }, [caricaCruscotto]);

  const inizializza = async () => {
    setAvvio(true);
    try {
      const { data } = await api.post("/sgi/inizializza");
      toast.success(`Sezione pronta: ${data.controlli_creati} schede di controllo precaricate`);
      await caricaCruscotto();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setAvvio(false);
    }
  };

  const schede = [
    { id: "cruscotto",   nome: "Cruscotto",       icona: ShieldCheck },
    { id: "organigramma", nome: "Organigramma",   icona: Network },
    { id: "scadenzario", nome: "Scadenzario",     icona: CalendarClock },
    { id: "controlli",   nome: "Controlli",       icona: ClipboardList },
    { id: "nc",          nome: "Non conformità",  icona: AlertTriangle },
    { id: "audit",       nome: "Audit",           icona: FileSignature },
    { id: "obiettivi",   nome: "Obiettivi",       icona: Flag },
    { id: "riesame",     nome: "Riesame",         icona: Scale },
    { id: "schede",      nome: "Schede e IO",     icona: ClipboardCheck },
    { id: "registri",    nome: "Registri",        icona: BookOpen },
    { id: "emergenze",   nome: "Emergenze",       icona: Flame },
    { id: "visite",      nome: "Visite esterne",  icona: ShieldQuestion },
    { id: "documenti",   nome: "Documenti",       icona: FileText },
    { id: "fascicolo",   nome: "Pacchetto audit", icona: FileArchive },
  ];

  return (
    <div className="space-y-6" data-testid="sistemi-integrati-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Sistemi di gestione</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Sistemi di gestione</h1>
          <p className="text-sm text-slate-500 mt-1">
            Otto certificazioni, un impianto solo: documenti in vigore, controlli e scadenze.
          </p>
        </div>
        {!riferimenti?.vede_riservati && (
          <div className="text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 max-w-xs">
            Le schede con dati riservati — retribuzioni, parità di genere, segnalazioni —
            sono visibili solo a direzione e amministrazione.
          </div>
        )}
      </div>

      {caricamento && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
        </p>
      )}

      {!caricamento && daPreparare && (
        <Card className="border-2 border-dashed border-orange-200 bg-orange-50/40">
          <CardContent className="p-8 text-center space-y-3">
            <ShieldCheck className="w-10 h-10 mx-auto text-orange-500" />
            <h2 className="font-display text-2xl font-black tracking-tight">La sezione è da preparare</h2>
            <p className="text-sm text-slate-600 max-w-xl mx-auto">
              Con un clic vengono creati gli otto sistemi certificati e circa quaranta schede
              di controllo già scritte: quelle che le norme chiedono, con periodicità e
              responsabile. Le potrai modificare, sospendere o integrare a piacere.
            </p>
            {puoScrivere ? (
              <Button onClick={inizializza} disabled={avvio}
                      className="bg-orange-500 hover:bg-orange-600 text-white h-12"
                      data-testid="prepara-sgi-btn">
                {avvio ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparazione...</>
                       : <><Plus className="w-4 h-4 mr-2" /> Prepara la sezione</>}
              </Button>
            ) : (
              <p className="text-xs text-slate-500">
                La preparazione la esegue la direzione o l&apos;amministrazione.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {!caricamento && !daPreparare && (
        <>
          <div className="flex gap-2 flex-wrap border-b border-slate-200 pb-3">
            {schede.map((s) => {
              const Icona = s.icona;
              const attiva = scheda === s.id;
              return (
                <button key={s.id} type="button" onClick={() => setScheda(s.id)}
                        data-testid={`scheda-${s.id}`}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                          attiva ? "bg-[#0067b2] text-white border-[#0067b2]"
                                 : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                  <Icona className="w-4 h-4" /> {s.nome}
                </button>
              );
            })}
          </div>

          {scheda === "cruscotto" && (
            <Cruscotto cruscotto={cruscotto}
                       puoScrivere={puoScrivere}
                       onAggiorna={caricaCruscotto}
                       onApriSistema={(codice) => { setSistemaSel(codice); setScheda("controlli"); }} />
          )}
          {scheda === "scadenzario" && (
            <Scadenzario sistemi={sistemi} sistemaSel={sistemaSel} setSistemaSel={setSistemaSel} />
          )}
          {scheda === "controlli" && (
            <Controlli sistemi={sistemi} riferimenti={riferimenti} puoScrivere={puoScrivere}
                       sistemaSel={sistemaSel} setSistemaSel={setSistemaSel}
                       onCambio={caricaCruscotto}
                       onApriRegistro={(codice) => {
                         setRegistroAperto(codice);
                         setScheda("registri");
                       }} />
          )}
          {scheda === "nc" && (
            <NonConformita sistemi={sistemi} riferimenti={riferimenti} puoScrivere={puoScrivere}
                           sistemaSel={sistemaSel} setSistemaSel={setSistemaSel}
                           onCambio={caricaCruscotto} />
          )}
          {scheda === "audit" && (
            <Audit sistemi={sistemi} riferimenti={riferimenti} puoScrivere={puoScrivere}
                   sistemaSel={sistemaSel} setSistemaSel={setSistemaSel} />
          )}
          {scheda === "obiettivi" && (
            <Obiettivi sistemi={sistemi} puoScrivere={puoScrivere}
                       sistemaSel={sistemaSel} setSistemaSel={setSistemaSel}
                       onCambio={caricaCruscotto} />
          )}
          {scheda === "riesame" && (
            <Riesame sistemi={sistemi} puoScrivere={puoScrivere} />
          )}
          {scheda === "schede" && (
            <SchedeOperative sistemi={sistemi} sistemaSel={sistemaSel}
                             setSistemaSel={setSistemaSel}
                             puoScrivere={puoScrivere}
                             daCompilare={daCompilare}
                             onAperta={() => setDaCompilare(null)} />
          )}
          {scheda === "registri" && (
            <Registri sistemi={sistemi} puoScrivere={puoScrivere}
                      sistemaSel={sistemaSel} setSistemaSel={setSistemaSel}
                      aperto={registroAperto} setAperto={setRegistroAperto} />
          )}
          {scheda === "emergenze" && <Emergenze puoScrivere={puoScrivere} />}
          {scheda === "visite" && <VisiteEsterne />}
          {scheda === "organigramma" && <Organigramma puoScrivere={puoScrivere} />}
          {scheda === "fascicolo" && (
            <Fascicolo sistemi={sistemi} sistemaSel={sistemaSel} setSistemaSel={setSistemaSel}
                       puoScrivere={puoScrivere} />
          )}
          {scheda === "documenti" && (
            <Documenti sistemi={sistemi} riferimenti={riferimenti} puoScrivere={puoScrivere}
                       sistemaSel={sistemaSel} setSistemaSel={setSistemaSel}
                       onCambio={caricaCruscotto}
                       onCompila={(slug) => {
                         setDaCompilare({ slug, persona: "" });
                         setScheda("schede");
                       }} />
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Cruscotto
// -----------------------------------------------------------------------------
function Cruscotto({ cruscotto, puoScrivere, onAggiorna, onApriSistema }) {
  const [modifica, setModifica] = useState(null);
  const t = cruscotto.totali || {};

  const numeri = [
    { valore: t.scaduti || 0,     nome: "Controlli scaduti",  classe: "text-red-600" },
    { valore: t.in_scadenza || 0, nome: "In scadenza a 30 giorni", classe: "text-amber-600" },
    { valore: t.da_avviare || 0,  nome: "Controlli mai eseguiti", classe: "text-sky-600" },
    { valore: t.nc_aperte || 0,   nome: "Non conformità aperte",
      classe: t.nc_in_ritardo ? "text-red-600" : "text-slate-800",
      nota: t.nc_in_ritardo ? `${t.nc_in_ritardo} oltre la scadenza` : null },
    { valore: t.obiettivi_aperti || 0, nome: "Obiettivi in corso", classe: "text-slate-800" },
    { valore: t.documenti_vigenti || 0, nome: "Documenti in vigore", classe: "text-slate-800" },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {numeri.map((n) => (
          <Card key={n.nome} className="border-slate-200">
            <CardContent className="p-4">
              <div className={`font-display text-3xl font-black tracking-tighter ${n.classe}`}>{n.valore}</div>
              <div className="text-xs text-slate-500 mt-1">{n.nome}</div>
              {n.nota && <div className="text-[11px] text-red-600 font-semibold mt-0.5">{n.nota}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-2">
        {cruscotto.sistemi.map((s) => {
          const giorni = giorniA(s.prossimo_audit);
          const vicino = giorni !== null && giorni <= 45;
          return (
            <Card key={s.codice}
                  className={`border-slate-200 ${vicino ? "border-orange-200 bg-orange-50/40" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <span className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                        style={{ background: s.colore }} />
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-semibold">{s.nome}</div>
                    <div className="text-xs text-slate-500">{s.oggetto}</div>
                    {s.ente && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {s.ente}{s.numero_certificato ? ` · ${s.numero_certificato}` : ""}
                      </div>
                    )}
                    {s.certificato_documento && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        Certificato:{" "}
                        <a href={fileUrl(s.certificato_documento.file_url)} target="_blank"
                           rel="noopener noreferrer" className="text-[#0067b2] hover:underline">
                          {s.certificato_documento.title}
                        </a>
                        {s.scadenza_certificato
                          ? ` · scade il ${dataIt(s.scadenza_certificato)}`
                          : " · senza data di scadenza"}
                      </div>
                    )}
                  </div>

                  <div className="min-w-[150px]">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Prossimo audit</div>
                    <div className={`text-sm font-medium ${vicino ? "text-orange-700" : "text-slate-700"}`}>
                      {meseAnno(s.prossimo_audit)}
                    </div>
                    {vicino && giorni !== null && (
                      <div className="text-[11px] text-orange-600 font-semibold">
                        {giorni <= 0 ? "questo mese" : `fra ${giorni} giorni`}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4 text-center">
                    <div>
                      <div className="font-display text-xl font-black text-slate-800">{s.controlli}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">controlli</div>
                    </div>
                    <div>
                      <div className={`font-display text-xl font-black ${s.scaduti ? "text-red-600" : "text-slate-300"}`}>
                        {s.scaduti}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">scaduti</div>
                    </div>
                    <div>
                      <div className={`font-display text-xl font-black ${s.nc_in_ritardo ? "text-red-600" : s.nc_aperte ? "text-amber-600" : "text-slate-300"}`}>
                        {s.nc_aperte || 0}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">non conf.</div>
                    </div>
                    <div>
                      <div className={`font-display text-xl font-black ${s.documenti_vigenti ? "text-slate-800" : "text-slate-300"}`}>
                        {s.documenti_vigenti}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">documenti</div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => onApriSistema(s.codice)}>
                      Apri
                    </Button>
                    {puoScrivere && (
                      <Button variant="outline" size="sm" onClick={() => setModifica(s)} title="Dati del certificato">
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {modifica && (
        <DialogSistema sistema={modifica} onChiudi={() => setModifica(null)}
                       onSalvato={() => { setModifica(null); onAggiorna(); }} />
      )}
    </div>
  );
}

function DialogSistema({ sistema, onChiudi, onSalvato }) {
  const [ente, setEnte] = useState(sistema.ente || "");
  const [numero, setNumero] = useState(sistema.numero_certificato || "");
  const [scadenza, setScadenza] = useState((sistema.scadenza_certificato || "").substring(0, 10));
  const [mesi, setMesi] = useState((sistema.mesi_audit || []).join(", "));
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    const elenco = mesi.split(",").map((m) => parseInt(m.trim(), 10)).filter((m) => m >= 1 && m <= 12);
    if (elenco.length === 0) { toast.error("Indica almeno un mese di audit (da 1 a 12)"); return; }
    setSalvo(true);
    try {
      await api.put(`/sgi/sistemi/${sistema.codice}`, {
        ente: ente || null,
        numero_certificato: numero || null,
        scadenza_certificato: scadenza || null,
        mesi_audit: elenco,
      });
      toast.success("Sistema aggiornato");
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>{sistema.nome} — {sistema.oggetto}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Ente di certificazione</Label>
            <Input value={ente} onChange={(e) => setEnte(e.target.value)} placeholder="es. RINA" /></div>
          <div><Label>Numero certificato</Label>
            <Input value={numero} onChange={(e) => setNumero(e.target.value)} /></div>
          <div><Label>Scadenza del certificato</Label>
            <Input type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} /></div>
          <div>
            <Label>Mesi in cui cade l&apos;audit</Label>
            <Input value={mesi} onChange={(e) => setMesi(e.target.value)} placeholder="es. 4, 10" />
            <p className="text-xs text-slate-500 mt-1">
              Numeri dei mesi separati da virgola. SA 8000 ne ha due: <span className="font-medium">4, 10</span>.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Scadenzario
// -----------------------------------------------------------------------------
function Scadenzario({ sistemi, sistemaSel, setSistemaSel }) {
  const [giorni, setGiorni] = useState(90);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCarico(true);
    const q = `?giorni=${giorni}${sistemaSel ? `&sistema=${sistemaSel}` : ""}`;
    api.get(`/sgi/scadenzario${q}`)
      .then(({ data }) => { if (vivo) setDati(data); })
      .catch(() => toast.error("Errore nel caricamento dello scadenzario"))
      .finally(() => { if (vivo) setCarico(false); });
    return () => { vivo = false; };
  }, [giorni, sistemaSel]);

  const perMese = useMemo(() => {
    const gruppi = [];
    for (const v of dati?.voci || []) {
      const chiave = String(v.scadenza).substring(0, 7);
      let g = gruppi.find((x) => x.chiave === chiave);
      if (!g) { g = { chiave, voci: [] }; gruppi.push(g); }
      g.voci.push(v);
    }
    return gruppi;
  }, [dati]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        {[30, 60, 90, 180, 365].map((g) => (
          <button key={g} type="button" onClick={() => setGiorni(g)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border ${
                    giorni === g ? "bg-[#0067b2] text-white border-[#0067b2]"
                                 : "bg-white text-slate-600 border-slate-200"}`}>
            {g === 365 ? "un anno" : `${g} giorni`}
          </button>
        ))}
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
      </div>

      {dati && (
        <div className="text-sm text-slate-500">
          {dati.voci.length} scadenze fino al {dataIt(dati.al)}
          {dati.scaduti > 0 && <span className="text-red-600 font-semibold"> · {dati.scaduti} già scadute</span>}
        </div>
      )}

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && (dati?.voci || []).length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessuna scadenza in questo periodo.
        </CardContent></Card>
      )}

      {perMese.map((g) => (
        <div key={g.chiave} className="space-y-2">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold pt-2">
            {meseAnno(`${g.chiave}-01`)}
          </div>
          {g.voci.map((v, i) => {
            const Icona = (TIPI_SCADENZA[v.tipo] || {}).icona || Clock;
            const s = STATI[v.stato] || STATI.non_programmato;
            return (
              <Card key={`${v.tipo}-${v.riferimento}-${i}`} className={`${s.bordo}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Icona className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-sm font-semibold">{v.titolo}</div>
                      <div className="text-xs text-slate-500">
                        {(TIPI_SCADENZA[v.tipo] || {}).nome || v.tipo}
                        {v.dettaglio ? ` · ${v.dettaglio}` : ""}
                        {v.responsabile ? ` · ${v.responsabile}` : ""}
                      </div>
                    </div>
                    <Tessere sistemi={sistemi} codici={v.sistemi} compatte />
                    <div className="text-right min-w-[92px]">
                      <div className="text-sm font-semibold tabular-nums">{dataIt(v.scadenza)}</div>
                      <Pallino stato={v.stato} />
                    </div>
                    {v.pagina && (
                      <Link to={v.pagina}>
                        <Button variant="outline" size="sm" title="Vai dove si registra">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function FiltroSistemi({ sistemi, valore, onCambia }) {
  return (
    <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
            value={valore} onChange={(e) => onCambia(e.target.value)}>
      <option value="">Tutti i sistemi</option>
      {sistemi.map((s) => <option key={s.codice} value={s.codice}>{s.nome}</option>)}
    </select>
  );
}

// -----------------------------------------------------------------------------
// Schede di controllo
// -----------------------------------------------------------------------------
function Controlli({ sistemi, riferimenti, puoScrivere, sistemaSel, setSistemaSel,
                     onCambio, onApriRegistro }) {
  const [righe, setRighe] = useState([]);
  const [carico, setCarico] = useState(true);
  const [ambito, setAmbito] = useState("");
  const [stato, setStato] = useState("");
  const [cerca, setCerca] = useState("");
  useDaCerca("cerca", setCerca);
  const [esegui, setEsegui] = useState(null);
  const [storico, setStorico] = useState(null);
  const [nuovo, setNuovo] = useState(false);
  const [modifica, setModifica] = useState(null);
  const [copertura, setCopertura] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (sistemaSel) p.set("sistema", sistemaSel);
      if (ambito) p.set("ambito", ambito);
      if (stato) p.set("stato", stato);
      const { data } = await api.get(`/sgi/controlli?${p.toString()}`);
      setRighe(data);
    } catch { toast.error("Errore nel caricamento dei controlli"); }
    finally { setCarico(false); }
  }, [sistemaSel, ambito, stato]);

  useEffect(() => { carica(); }, [carica]);

  const visibili = useMemo(() => {
    const q = cerca.trim().toLowerCase();
    if (!q) return righe;
    return righe.filter((c) => `${c.titolo} ${c.descrizione || ""}`.toLowerCase().includes(q));
  }, [righe, cerca]);

  const sospendi = async (c) => {
    if (!window.confirm(
      `Sospendere la scheda "${c.titolo}"?\n\n` +
      `Esce dagli elenchi e dallo scadenzario. Le esecuzioni già registrate restano in archivio.`
    )) return;
    try {
      await api.delete(`/sgi/controlli/${c.id}`);
      toast.success("Scheda sospesa");
      carica(); onCambio();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={ambito} onChange={(e) => setAmbito(e.target.value)}>
          <option value="">Tutti gli ambiti</option>
          {(riferimenti?.ambiti || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={stato} onChange={(e) => setStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.nome}</option>)}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca..."
                 className="h-9 border border-slate-200 rounded-full pl-8 pr-3 text-xs w-44" />
        </div>
        <span className="text-xs text-slate-400 ml-auto">{visibili.length} schede</span>
        {puoScrivere && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuovo(true)}>
            <Plus className="w-3 h-3 mr-1" /> Nuova scheda
          </Button>
        )}
      </div>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && visibili.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessuna scheda con questi filtri.
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {visibili.map((c) => {
          const s = STATI[c.stato] || STATI.non_programmato;
          return (
            <Card key={c.id} className={s.bordo}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{c.titolo}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.periodicita_nome} · {c.ambito} · {c.responsabile}
                    </div>
                  </div>
                  <Pallino stato={c.stato} />
                </div>

                {c.descrizione && <p className="text-xs text-slate-500">{c.descrizione}</p>}

                {c.riservato && (
                  <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-0.5">
                    <Shield className="w-3 h-3" /> Riservato
                  </div>
                )}

                {c.fonte_nome && (
                  <button type="button" onClick={() => setCopertura(c)}
                          data-testid={`copertura-${c.id}`}
                          className="w-full text-left text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded px-2 py-1.5 flex items-center gap-2 flex-wrap transition-colors">
                    <span className="text-slate-500">Il dato lo raccoglie già</span>
                    <span className="font-medium text-slate-700">{c.fonte_nome}</span>
                    <span className="text-[#0067b2] font-medium inline-flex items-center gap-0.5 ml-auto">
                      guarda cosa manca <ChevronRight className="w-3 h-3" />
                    </span>
                  </button>
                )}

                {c.registro_nome && (
                  <button type="button" onClick={() => onApriRegistro(c.registro)}
                          data-testid={`registro-${c.id}`}
                          className="w-full text-left text-xs bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded px-2 py-1.5 flex items-center gap-2 flex-wrap transition-colors">
                    <BookOpen className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-slate-500">Si compila su</span>
                    <span className="font-medium text-slate-700">{c.registro_nome}</span>
                    <span className="text-[#0067b2] font-medium inline-flex items-center gap-0.5 ml-auto">
                      apri <ChevronRight className="w-3 h-3" />
                    </span>
                  </button>
                )}

                <Tessere sistemi={sistemi} codici={c.sistemi} />

                <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                  <div className="text-xs text-slate-500">
                    {c.ultima_esecuzione
                      ? <>Ultima: <span className="font-medium">{dataIt(c.ultima_esecuzione)}</span>
                          {" · "}Prossima: <span className="font-medium">{dataIt(c.prossima_scadenza)}</span></>
                      : <span className="text-sky-700">Mai eseguito</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setEsegui(c)}
                            data-testid={`esegui-${c.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Registra
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setStorico(c)} title="Storico">
                      <History className="w-3 h-3" />
                    </Button>
                    {puoScrivere && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setModifica(c)} title="Modifica">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => sospendi(c)}
                                className="text-red-600" title="Sospendi">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {esegui && (
        <DialogEsecuzione controllo={esegui} esiti={riferimenti?.esiti || []}
                          onChiudi={() => setEsegui(null)}
                          onSalvato={() => { setEsegui(null); carica(); onCambio(); }} />
      )}
      {storico && (
        <DialogStorico controllo={storico} esiti={riferimenti?.esiti || []}
                       onChiudi={() => setStorico(null)}
                       onCambio={() => { carica(); onCambio(); }} />
      )}
      {copertura && (
        <DialogCopertura controllo={copertura} puoScrivere={puoScrivere}
                         onChiudi={() => setCopertura(null)}
                         onEsegui={() => { setEsegui(copertura); setCopertura(null); }} />
      )}
      {(nuovo || modifica) && (
        <DialogControllo controllo={modifica} sistemi={sistemi} riferimenti={riferimenti}
                         onChiudi={() => { setNuovo(false); setModifica(null); }}
                         onSalvato={() => { setNuovo(false); setModifica(null); carica(); onCambio(); }} />
      )}
    </div>
  );
}

// Quanto di un controllo risulta gia' fatto, leggendolo dove il dato sta.
// Non si ricopia niente: si vede cosa manca, per nome.
function DialogCopertura({ controllo, puoScrivere, onChiudi, onEsegui }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [soloMancanti, setSoloMancanti] = useState(true);
  const [taratura, setTaratura] = useState(null);
  const [gestisci, setGestisci] = useState(false);

  const carica = useCallback(() => {
    setDati(null);
    api.get(`/sgi/controlli/${controllo.id}/copertura`)
      .then(({ data }) => setDati(data))
      .catch((e) => setErrore(e.response?.data?.detail || "Errore"));
  }, [controllo.id]);

  useEffect(() => { carica(); }, [carica]);

  const visibili = useMemo(() => {
    if (!dati) return [];
    return soloMancanti ? dati.voci.filter((v) => v.stato !== "coperto") : dati.voci;
  }, [dati, soloMancanti]);

  const perc = dati?.percentuale;
  const colore = perc === null || perc === undefined ? "#cbd5e1"
    : perc >= 90 ? "#059669" : perc >= 60 ? "#d97706" : "#dc2626";

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{controllo.titolo}</DialogTitle>
        </DialogHeader>

        {errore && <p className="text-sm text-red-600 py-4">{errore}</p>}
        {!dati && !errore && <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>}

        {dati && (
          <>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
              <div className="text-xs text-slate-500">
                Letto da <span className="font-medium text-slate-700">{dati.fonte_nome}</span>
                {" · "}{dati.cosa}
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-3xl font-black tabular-nums" style={{ color: colore }}>
                  {perc === null ? "—" : `${perc}%`}
                </span>
                <span className="text-sm text-slate-600">
                  {dati.totale - dati.scoperti} su {dati.totale} a posto
                  {dati.anomalie > 0 && (
                    <span className="text-amber-700"> · {dati.anomalie} da guardare</span>
                  )}
                </span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all"
                     style={{ width: `${perc || 0}%`, background: colore }} />
              </div>
              <p className="text-xs text-slate-500">{dati.nota}</p>
            </div>

            {dati.obiettivo && <Obiettivo dato={dati.obiettivo} />}

            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => setSoloMancanti(!soloMancanti)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                        soloMancanti ? "bg-[#0067b2] text-white border-[#0067b2]"
                                     : "bg-white text-slate-600 border-slate-200"}`}>
                {soloMancanti ? "Solo quello che manca" : "Tutti"}
              </button>
              <span className="text-xs text-slate-400">{visibili.length} voci</span>
              {dati.fonte === "taratura" && puoScrivere && (
                <Button size="sm" variant="outline" className="ml-auto"
                        onClick={() => setGestisci(true)}>
                  <Gauge className="w-3 h-3 mr-1" /> Scegli gli strumenti
                </Button>
              )}
              {dati.pagina && dati.fonte !== "taratura" && (
                <Link to={dati.pagina} className="ml-auto">
                  <Button size="sm" variant="outline">
                    <ExternalLink className="w-3 h-3 mr-1" /> Vai alla pagina
                  </Button>
                </Link>
              )}
            </div>

            {visibili.length === 0 && (
              <p className="text-sm text-emerald-700 py-6 text-center font-medium">
                Niente da recuperare: è tutto a posto.
              </p>
            )}

            <div className="space-y-1 max-h-[42vh] overflow-y-auto">
              {visibili.map((v) => {
                const s = COPERTURA[v.stato] || COPERTURA.coperto;
                return (
                  <div key={`${v.riferimento}-${v.nome}`}
                       className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: s.punto }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{v.nome}</div>
                      {v.dettaglio && (
                        <div className="text-xs text-slate-500 truncate">{v.dettaglio}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${s.classe}`}>
                        {v.nota}
                      </div>
                      {v.data && (
                        <div className="text-[11px] text-slate-400 tabular-nums mt-0.5">{dataIt(v.data)}</div>
                      )}
                    </div>
                    {dati.fonte === "taratura" && puoScrivere ? (
                      <Button size="sm" variant="outline" onClick={() => setTaratura(v)}
                              title="Registra la taratura">
                        <Gauge className="w-3 h-3" />
                      </Button>
                    ) : v.pagina ? (
                      <Link to={v.pagina}>
                        <Button size="sm" variant="outline" title="Apri">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </Link>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          <Button onClick={onEsegui} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Check className="w-3 h-3 mr-1" /> Registra il controllo
          </Button>
        </DialogFooter>

        {taratura && (
          <DialogTaratura voce={taratura} onChiudi={() => setTaratura(null)}
                          onSalvato={() => { setTaratura(null); carica(); }} />
        )}
        {gestisci && (
          <DialogStrumenti onChiudi={() => setGestisci(false)}
                           onCambio={() => { setGestisci(false); carica(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * L'obiettivo di volume: due verifiche al mese.
 *
 * Non colora niente di rosso apposta. Un mese sotto obiettivo non e' una non
 * conformita': e' un dato non completo, e la parola giusta cambia cosa succede
 * dopo - si porta in riunione con i tecnici invece di aprire un'azione
 * correttiva.
 */
function Obiettivo({ dato }) {
  const ora = dato.mese_corrente;
  return (
    <div className="border border-sky-200 bg-sky-50/60 rounded p-3 space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-sky-800">
          Obiettivo: {dato.quante} al {dato.periodo}
        </span>
        <span className="text-sm text-slate-600">
          questo mese <span className="font-semibold tabular-nums">{ora.fatte}</span> su {ora.attese}
        </span>
      </div>
      <div className="flex gap-1">
        {dato.ultimi_mesi.map((m) => (
          <div key={m.mese} className="flex-1 text-center" title={`${m.nome}: ${m.fatte}`}>
            <div className={`h-8 rounded flex items-end justify-center text-[10px] font-semibold ${
              m.completo ? "bg-emerald-500 text-white"
                         : m.corrente ? "bg-sky-200 text-sky-900"
                                      : "bg-slate-200 text-slate-600"}`}>
              <span className="pb-1 tabular-nums">{m.fatte}</span>
            </div>
            <div className="text-[9px] text-slate-400 mt-0.5 truncate">
              {m.nome.split(" ")[0].slice(0, 3)}
            </div>
          </div>
        ))}
      </div>
      {dato.da_discutere.length > 0 ? (
        <p className="text-xs text-sky-900">
          Sotto obiettivo: {dato.da_discutere.join(", ")}. {dato.nota}
        </p>
      ) : (
        <p className="text-xs text-slate-500">
          Obiettivo raggiunto in tutti i mesi chiusi degli ultimi sei.
        </p>
      )}
    </div>
  );
}

function DialogTaratura({ voce, onChiudi, onSalvato }) {
  const oggi = new Date().toISOString().substring(0, 10);
  const [data, setData] = useState(oggi);
  const [ente, setEnte] = useState(voce.ente || "");
  const [certificato, setCertificato] = useState("");
  const [costo, setCosto] = useState("");
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    setSalvo(true);
    try {
      await api.post(`/sgi/taratura/${voce.riferimento}/registra`, {
        data, ente: ente || null, certificato: certificato || null,
        costo: costo === "" ? null : Number(costo),
      });
      toast.success("Taratura registrata");
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  // Il dialogo sta dentro un altro dialogo: senza pointer-events e z-index
  // espliciti resta visibile ma non riceve i tocchi.
  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="z-[60]" style={{ pointerEvents: "auto" }}>
        <DialogHeader><DialogTitle>Taratura di {voce.nome}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data della taratura</Label>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)} /></div>
            <div><Label>Costo</Label>
              <Input type="number" step="0.01" value={costo}
                     onChange={(e) => setCosto(e.target.value)} placeholder="€" /></div>
          </div>
          <div><Label>Laboratorio</Label>
            <Input value={ente} onChange={(e) => setEnte(e.target.value)}
                   placeholder="es. LAT 051" /></div>
          <div><Label>Numero del certificato</Label>
            <Input value={certificato} onChange={(e) => setCertificato(e.target.value)}
                   placeholder="es. C1191FF830" /></div>
          <p className="text-xs text-slate-500">
            Resta anche nel libro di manutenzione dello strumento, dove in audit
            la taratura si va a cercare. La prossima scadenza si sposta da sola.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogStrumenti({ onChiudi, onCambio }) {
  const [righe, setRighe] = useState(null);
  const [mesi, setMesi] = useState(12);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/sgi/taratura/candidati")
      .then(({ data }) => setRighe(data))
      .catch(() => setRighe([]));
  }, []);

  const accendi = async (r) => {
    setBusy(r.id);
    try {
      await api.put(`/sgi/taratura/${r.id}`, { attiva: true, intervallo_mesi: Number(mesi) });
      toast.success(`${r.identifier}: taratura ogni ${mesi} mesi`);
      setRighe((prec) => prec.filter((x) => x.id !== r.id));
      onCambio();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="z-[60]" style={{ pointerEvents: "auto" }}>
        <DialogHeader><DialogTitle>Strumenti da tenere sotto taratura</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">
          I primi sono quelli che di solito la richiedono, ma la taratura si può
          accendere su qualunque attrezzatura.
        </p>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Ogni</Label>
          <select className="h-9 border border-slate-200 rounded px-2 text-sm" value={mesi}
                  onChange={(e) => setMesi(e.target.value)}>
            {[6, 12, 24, 36].map((m) => <option key={m} value={m}>{m} mesi</option>)}
          </select>
        </div>
        {righe === null && <p className="text-sm text-slate-500 py-4">Caricamento...</p>}
        {righe && righe.length === 0 && (
          <p className="text-sm text-slate-500 py-4">
            Tutte le attrezzature attive hanno già la taratura accesa.
          </p>
        )}
        {righe && righe.length > 0 && (
          <div className="space-y-1 max-h-[45vh] overflow-y-auto">
            {righe.map((r) => (
              <div key={r.id} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {r.identifier}
                    {r.suggerito && (
                      <span className="ml-2 text-[10px] font-semibold text-[#0067b2] bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">
                        consigliato
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {[r.brand, r.model, r.category].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled={busy === r.id}
                        onClick={() => accendi(r)}>
                  {busy === r.id ? "..." : "Accendi"}
                </Button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onChiudi}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogEsecuzione({ controllo, esiti, onChiudi, onSalvato, esecuzione = null }) {
  const oggi = new Date().toISOString().substring(0, 10);
  // Correggendo una registrazione si usano i campi con cui era stata fatta:
  // la scheda nel frattempo puo' essere cambiata.
  const campi = (esecuzione && esecuzione.campi) || controllo.campi || [];
  const [data, setData] = useState(esecuzione?.data || oggi);
  const [esito, setEsito] = useState(esecuzione?.esito || "conforme");
  const [note, setNote] = useState(esecuzione?.note || "");
  const [salvo, setSalvo] = useState(false);
  const [persone, setPersone] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [valori, setValori] = useState(() => {
    const partenza = {};
    for (const c of campi) partenza[c.codice] = c.tipo === "si_no" ? false : "";
    return { ...partenza, ...((esecuzione && esecuzione.valori) || {}) };
  });

  const servePersone = campi.some((c) => c.tipo === "persona");
  const serveMezzi = campi.some((c) => c.tipo === "mezzo");

  useEffect(() => {
    if (servePersone) api.get("/employees").then(({ data: d }) => setPersone(d)).catch(() => {});
    if (serveMezzi) api.get("/equipment").then(({ data: d }) => setMezzi(d)).catch(() => {});
  }, [servePersone, serveMezzi]);

  const cambia = (codice, v) => setValori((p) => ({ ...p, [codice]: v }));

  const nomiEsito = { conforme: "Conforme", osservazione: "Osservazione", non_conforme: "Non conforme" };

  // Il modulo si manda solo se e' stato toccato: chi vuole limitarsi a
  // segnare che il controllo e' stato fatto deve poterlo ancora fare.
  const compilato = campi.some((c) => {
    const v = valori[c.codice];
    return c.tipo === "si_no" ? v === true : v !== "" && v !== null && v !== undefined;
  });

  const salva = async () => {
    if (compilato) {
      const mancante = campi.find(
        (c) => c.obbligatorio && c.tipo !== "si_no"
               && (valori[c.codice] === "" || valori[c.codice] === null
                   || valori[c.codice] === undefined));
      if (mancante) { toast.error(`Manca il campo "${mancante.nome}"`); return; }
    }
    setSalvo(true);
    try {
      const corpo = { data, esito, note: note || null, valori: compilato ? valori : null };
      if (esecuzione) {
        await api.put(`/sgi/controlli/${controllo.id}/esecuzioni/${esecuzione.id}`, corpo);
        toast.success("Registrazione corretta");
      } else {
        await api.post(`/sgi/controlli/${controllo.id}/esecuzioni`, corpo);
        toast.success("Controllo registrato");
      }
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      {/* Aperto sopra lo storico, che e' gia' un dialogo: sta un piano piu' su
          e riceve i tocchi (vedi CampoFirma). */}
      <DialogContent className={`${campi.length ? "max-h-[88vh] overflow-y-auto" : ""} ${esecuzione ? "z-[60]" : ""}`.trim() || undefined}
                     style={esecuzione ? { pointerEvents: "auto" } : undefined}>
        <DialogHeader>
          <DialogTitle>{esecuzione ? "Correggi la registrazione" : "Registra il controllo"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm font-medium">{controllo.titolo}</p>
          <div><Label>Data di esecuzione</Label>
            <Input type="date" value={data} onChange={(e) => setData(e.target.value)} /></div>

          {campi.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">
                Modulo della scheda
              </div>
              {campi.map((c) => (
                <div key={c.codice}>
                  {c.tipo !== "si_no" && (
                    <Label>{c.nome}{c.obbligatorio ? " *" : ""}</Label>
                  )}
                  <CampoRegistro campo={c} valore={valori[c.codice]}
                                 onCambia={(v) => cambia(c.codice, v)}
                                 persone={persone} mezzi={mezzi} />
                  {c.nota && <p className="text-xs text-slate-500 mt-1">{c.nota}</p>}
                </div>
              ))}
              <p className="text-xs text-slate-500">
                Puoi lasciarlo in bianco e registrare solo la data: quello che compili
                finisce nel pacchetto per l&apos;audit.
              </p>
            </div>
          )}
          <div>
            <Label>Esito</Label>
            <div className="flex gap-2 mt-1">
              {(esiti.length ? esiti : ["conforme", "osservazione", "non_conforme"]).map((e) => (
                <button key={e} type="button" onClick={() => setEsito(e)}
                        className={`flex-1 px-3 py-2 rounded border text-xs font-medium ${
                          esito === e
                            ? e === "non_conforme" ? "bg-red-500 text-white border-red-500"
                              : e === "osservazione" ? "bg-amber-500 text-white border-amber-500"
                              : "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-600 border-slate-200"}`}>
                  {nomiEsito[e] || e}
                </button>
              ))}
            </div>
          </div>
          <div><Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="Cosa è stato verificato, eventuali rilievi" /></div>
          <p className="text-xs text-slate-500">
            La prossima scadenza si sposta automaticamente in avanti di{" "}
            <span className="font-medium">{(controllo.periodicita_nome || "").toLowerCase()}</span>.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : esecuzione ? "Salva la correzione" : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogStorico({ controllo, esiti = [], onChiudi, onCambio }) {
  const { user } = useAuth();
  const [righe, setRighe] = useState(null);
  const [correggi, setCorreggi] = useState(null);
  const carica = useCallback(() => {
    api.get(`/sgi/controlli/${controllo.id}/esecuzioni`)
      .then(({ data }) => setRighe(data))
      .catch(() => setRighe([]));
  }, [controllo.id]);
  useEffect(() => { carica(); }, [carica]);

  // Una registrazione sbagliata la corregge chi l'ha fatta, o la direzione.
  const puoToccare = (e) => user?.role === "admin" || user?.role === "amministrativo"
                            || e.eseguito_da === user?.email;

  const elimina = async (e) => {
    if (!window.confirm(`Eliminare la registrazione del ${dataIt(e.data)}? La scadenza torna a quella precedente.`)) return;
    try {
      await api.delete(`/sgi/controlli/${controllo.id}/esecuzioni/${e.id}`);
      toast.success("Registrazione eliminata");
      carica();
      onCambio && onCambio();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const colore = { conforme: "text-emerald-700", osservazione: "text-amber-700", non_conforme: "text-red-600" };
  const nomi = { conforme: "Conforme", osservazione: "Osservazione", non_conforme: "Non conforme" };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Storico · {controllo.titolo}</DialogTitle></DialogHeader>
        {righe === null && <p className="text-sm text-slate-500 py-4">Caricamento...</p>}
        {righe && righe.length === 0 && (
          <p className="text-sm text-slate-500 py-4">Nessuna esecuzione registrata finora.</p>
        )}
        {righe && righe.length > 0 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {righe.map((e) => (
              <div key={e.id} className="border border-slate-200 rounded p-3">
                <div className="flex justify-between gap-2 items-center">
                  <span className="text-sm font-semibold tabular-nums">{dataIt(e.data)}</span>
                  <span className={`text-xs font-semibold ${colore[e.esito] || ""}`}>
                    {nomi[e.esito] || e.esito}
                  </span>
                </div>
                {e.valori && (e.campi || []).length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                    {e.campi.map((c) => {
                      const v = e.valori[c.codice];
                      if (v === null || v === undefined || v === "") return null;
                      const etichetta = (e.etichette || {})[c.codice];
                      return (
                        <div key={c.codice} className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">
                            {c.nome}
                          </div>
                          <div className="text-xs break-words">
                            {c.tipo === "si_no" ? (v ? "sì" : "no")
                              : etichetta || String(v)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {e.note && <p className="text-xs text-slate-500 mt-1">{e.note}</p>}
                <div className="flex items-center justify-between gap-2 mt-1">
                  <p className="text-[10px] text-slate-400">
                    {e.eseguito_da}{e.modificato_il ? " · corretta" : ""}
                  </p>
                  {puoToccare(e) && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => setCorreggi(e)} title="Correggi"
                              data-testid={`esecuzione-modifica-${e.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Modifica
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600"
                              onClick={() => elimina(e)} title="Elimina"
                              data-testid={`esecuzione-elimina-${e.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Elimina
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onChiudi}>Chiudi</Button></DialogFooter>
      </DialogContent>
      {correggi && (
        <DialogEsecuzione controllo={controllo} esiti={esiti} esecuzione={correggi}
                          onChiudi={() => setCorreggi(null)}
                          onSalvato={() => { setCorreggi(null); carica(); onCambio && onCambio(); }} />
      )}
    </Dialog>
  );
}

function SceltaSistemi({ sistemi, scelti, onCambia }) {
  const alterna = (codice) => {
    onCambia(scelti.includes(codice) ? scelti.filter((c) => c !== codice) : [...scelti, codice]);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {sistemi.map((s) => {
        const attivo = scelti.includes(s.codice);
        return (
          <button key={s.codice} type="button" onClick={() => alterna(s.codice)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${
                    attivo ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200"}`}
                  style={attivo ? { background: s.colore } : undefined}>
            <span className="w-1.5 h-1.5 rounded-full"
                  style={{ background: attivo ? "#fff" : s.colore }} />
            {s.nome}
          </button>
        );
      })}
    </div>
  );
}

// Selezione a pastiglie di una lista qualunque (categorie, tipi, ...).
function SceltaVoci({ voci, scelte, onCambia }) {
  const alterna = (v) => {
    onCambia(scelte.includes(v) ? scelte.filter((x) => x !== v) : [...scelte, v]);
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {voci.map((v) => {
        const attiva = scelte.includes(v);
        return (
          <button key={v} type="button" onClick={() => alterna(v)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${
                    attiva ? "bg-[#0067b2] text-white border-[#0067b2]"
                           : "bg-white text-slate-600 border-slate-200"}`}>
            {v}
          </button>
        );
      })}
    </div>
  );
}

function DialogControllo({ controllo, sistemi, riferimenti, onChiudi, onSalvato }) {
  const modifica = Boolean(controllo);
  const [titolo, setTitolo] = useState(controllo?.titolo || "");
  const [descrizione, setDescrizione] = useState(controllo?.descrizione || "");
  const [ambito, setAmbito] = useState(controllo?.ambito || "Azienda");
  const [periodicita, setPeriodicita] = useState(controllo?.periodicita || "annuale");
  const [responsabile, setResponsabile] = useState(controllo?.responsabile || "Direzione");
  const [scelti, setScelti] = useState(controllo?.sistemi || []);
  const [categorie, setCategorie] = useState(controllo?.categorie || []);
  const [riservato, setRiservato] = useState(Boolean(controllo?.riservato));
  const [salvo, setSalvo] = useState(false);

  // Le categorie restringono solo i controlli letti dai libri di manutenzione:
  // altrove non vogliono dire niente.
  const limitabile = controllo?.fonte === "manutenzioni";

  const salva = async () => {
    if (!titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (scelti.length === 0) { toast.error("Indica almeno un sistema"); return; }
    setSalvo(true);
    const corpo = {
      titolo: titolo.trim(), descrizione: descrizione || null, ambito,
      periodicita, responsabile, sistemi: scelti, riservato,
      ...(limitabile ? { categorie } : {}),
    };
    try {
      if (modifica) {
        await api.put(`/sgi/controlli/${controllo.id}`, corpo);
        toast.success("Scheda aggiornata");
      } else {
        await api.post("/sgi/controlli", corpo);
        toast.success("Scheda creata");
      }
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modifica ? "Modifica scheda di controllo" : "Nuova scheda di controllo"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Titolo *</Label>
            <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                   placeholder="es. Verifica impianto fotovoltaico" /></div>
          <div><Label>Descrizione</Label>
            <Input value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
                   placeholder="Cosa si verifica, e come" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ambito</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={ambito}
                      onChange={(e) => setAmbito(e.target.value)}>
                {(riferimenti?.ambiti || []).map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <div><Label>Responsabile</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={responsabile}
                      onChange={(e) => setResponsabile(e.target.value)}>
                {(riferimenti?.responsabili || []).map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div><Label>Ogni quanto</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={periodicita}
                    onChange={(e) => setPeriodicita(e.target.value)}>
              {(riferimenti?.periodicita || []).map((p) => (
                <option key={p.codice} value={p.codice}>{p.nome}</option>
              ))}
            </select>
          </div>
          <div><Label>Sistemi a cui risponde *</Label>
            <div className="mt-1"><SceltaSistemi sistemi={sistemi} scelti={scelti} onCambia={setScelti} /></div>
          </div>
          {limitabile && (
            <div>
              <Label>Limita alle categorie</Label>
              <div className="mt-1">
                <SceltaVoci voci={riferimenti?.categorie_attrezzature || []}
                            scelte={categorie} onCambia={setCategorie} />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Senza nessuna categoria selezionata il controllo vale per tutti
                i mezzi e le attrezzature attive.
              </p>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={riservato} onChange={(e) => setRiservato(e.target.checked)} />
            <span>Riservato — visibile solo a direzione e amministrazione</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : modifica ? "Salva modifiche" : "Crea scheda"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Non conformità e azioni correttive
// -----------------------------------------------------------------------------
function NonConformita({ sistemi, riferimenti, puoScrivere, sistemaSel, setSistemaSel, onCambio }) {
  const [righe, setRighe] = useState([]);
  const [carico, setCarico] = useState(true);
  const [stato, setStato] = useState("");
  const [origine, setOrigine] = useState("");
  const [daValutare, setDaValutare] = useState([]);
  const [apriElenco, setApriElenco] = useState(false);
  const [aperta, setAperta] = useState(null);
  const [nuova, setNuova] = useState(false);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (sistemaSel) p.set("sistema", sistemaSel);
      if (stato) p.set("stato", stato);
      if (origine) p.set("origine", origine);
      const [n, s] = await Promise.all([
        api.get(`/sgi/nc?${p.toString()}`),
        api.get("/sgi/nc/da-segnalazioni").catch(() => ({ data: [] })),
      ]);
      setRighe(n.data);
      setDaValutare(s.data);
    } catch { toast.error("Errore nel caricamento delle non conformità"); }
    finally { setCarico(false); }
  }, [sistemaSel, stato, origine]);

  useEffect(() => { carica(); }, [carica]);

  const rinfresca = () => { carica(); onCambio(); };

  return (
    <div className="space-y-4">
      {daValutare.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/60">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-sm">
                {daValutare.length} segnalazion{daValutare.length === 1 ? "e" : "i"} dal campo da valutare
              </div>
              <div className="text-xs text-slate-600">
                Non conformità e segnalazioni gravi che non sono ancora diventate un&apos;azione correttiva.
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setApriElenco(true)}>
              Guarda
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={stato} onChange={(e) => setStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATI_NC).map(([k, v]) => <option key={k} value={k}>{v.nome}</option>)}
        </select>
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={origine} onChange={(e) => setOrigine(e.target.value)}>
          <option value="">Tutte le origini</option>
          {(riferimenti?.origini_nc || []).map((o) => (
            <option key={o.codice} value={o.codice}>{o.nome}</option>
          ))}
        </select>
        <span className="text-xs text-slate-400 ml-auto">{righe.length} rilievi</span>
        {puoScrivere && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuova(true)} data-testid="nuova-nc">
            <Plus className="w-3 h-3 mr-1" /> Nuovo rilievo
          </Button>
        )}
      </div>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && righe.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessun rilievo con questi filtri.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {righe.map((n) => {
          const s = STATI_NC[n.stato] || STATI_NC.aperta;
          return (
            <Card key={n.id}
                  className={n.in_ritardo ? "border-red-200 bg-red-50/60" : "border-slate-200"}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs font-semibold text-slate-500">{n.numero}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.classe}`}>
                        {s.nome}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${GRAVITA_UI[n.gravita] || GRAVITA_UI.bassa}`}>
                        {n.gravita}
                      </span>
                      <Badge variant="outline" className="text-[10px]">{n.tipo_nome}</Badge>
                      {n.riservato && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">
                          <Shield className="w-2.5 h-2.5" /> riservato
                        </span>
                      )}
                    </div>
                    <div className="text-sm">{n.descrizione}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {n.origine_nome_norma}
                      {n.origine_nome ? ` · ${n.origine_nome}` : ""}
                      {" · rilevata il "}{dataIt(n.rilevata_il)}
                    </div>
                  </div>
                  <div className="min-w-[130px]">
                    {n.scadenza && (
                      <>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Scadenza azione</div>
                        <div className={`text-sm font-semibold tabular-nums ${n.in_ritardo ? "text-red-600" : ""}`}>
                          {dataIt(n.scadenza)}
                        </div>
                      </>
                    )}
                    {n.responsabile && (
                      <div className="text-xs text-slate-500">{n.responsabile}</div>
                    )}
                  </div>
                  <Tessere sistemi={sistemi} codici={n.sistemi} compatte />
                  <Button size="sm" variant="outline" onClick={() => setAperta(n)}>
                    Apri
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {apriElenco && (
        <DialogSegnalazioni righe={daValutare} puoScrivere={puoScrivere}
                            onChiudi={() => setApriElenco(false)}
                            onCreata={rinfresca} />
      )}
      {aperta && (
        <DialogNonConformita nc={aperta} sistemi={sistemi} puoScrivere={puoScrivere}
                             onChiudi={() => setAperta(null)}
                             onCambio={() => { setAperta(null); rinfresca(); }} />
      )}
      {nuova && (
        <DialogNuovaNC sistemi={sistemi} riferimenti={riferimenti}
                       onChiudi={() => setNuova(false)}
                       onSalvato={() => { setNuova(false); rinfresca(); }} />
      )}
    </div>
  );
}

function DialogSegnalazioni({ righe, puoScrivere, onChiudi, onCreata }) {
  const [busy, setBusy] = useState(null);

  const apri = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/sgi/nc/da-segnalazione/${r.id}`);
      toast.success(`Aperta la ${data.numero}`);
      onCreata();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(null); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Segnalazioni dal campo da valutare</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">
          Aprendo un rilievo, descrizione, data, gravità e cantiere vengono ricopiati da qui.
        </p>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {righe.map((r) => (
            <div key={r.id} className="border border-slate-200 rounded p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px]">{r.tipo}</Badge>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${GRAVITA_UI[r.gravita] || GRAVITA_UI.bassa}`}>
                  {r.gravita}
                </span>
                <span className="text-xs text-slate-500 ml-auto tabular-nums">{dataIt(r.data)}</span>
              </div>
              <div className="text-sm mt-1">{r.descrizione}</div>
              <div className="flex items-center justify-between gap-2 mt-2">
                <span className="text-xs text-slate-500">{r.cantiere || "Sede"}</span>
                {puoScrivere && (
                  <Button size="sm" variant="outline" disabled={busy === r.id}
                          onClick={() => apri(r)}>
                    {busy === r.id ? "..." : "Apri rilievo"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onChiudi}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogNuovaNC({ sistemi, riferimenti, onChiudi, onSalvato }) {
  const oggi = new Date().toISOString().substring(0, 10);
  const [descrizione, setDescrizione] = useState("");
  const [tipo, setTipo] = useState("non_conformita");
  const [origine, setOrigine] = useState("processo");
  const [gravita, setGravita] = useState("media");
  const [rilevata, setRilevata] = useState(oggi);
  const [scelti, setScelti] = useState([]);
  const [riservato, setRiservato] = useState(false);
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    if (!descrizione.trim()) { toast.error("La descrizione è obbligatoria"); return; }
    if (scelti.length === 0) { toast.error("Indica almeno un sistema"); return; }
    setSalvo(true);
    try {
      const { data } = await api.post("/sgi/nc", {
        descrizione: descrizione.trim(), tipo, origine, gravita,
        rilevata_il: rilevata, sistemi: scelti, riservato,
      });
      toast.success(`Aperta la ${data.numero}`);
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Nuovo rilievo</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Descrizione *</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[70px]"
                      value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
                      placeholder="Che cosa è stato rilevato" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tipo</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={tipo}
                      onChange={(e) => setTipo(e.target.value)}>
                {(riferimenti?.tipi_nc || []).map((t) => (
                  <option key={t.codice} value={t.codice}>{t.nome}</option>
                ))}
              </select>
            </div>
            <div><Label>Gravità</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={gravita}
                      onChange={(e) => setGravita(e.target.value)}>
                {(riferimenti?.gravita || ["bassa", "media", "alta"]).map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Da dove nasce</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={origine}
                      onChange={(e) => setOrigine(e.target.value)}>
                {(riferimenti?.origini_nc || []).map((o) => (
                  <option key={o.codice} value={o.codice}>{o.nome}</option>
                ))}
              </select>
            </div>
            <div><Label>Rilevata il</Label>
              <Input type="date" value={rilevata} onChange={(e) => setRilevata(e.target.value)} /></div>
          </div>
          <div><Label>Sistemi interessati *</Label>
            <div className="mt-1"><SceltaSistemi sistemi={sistemi} scelti={scelti} onCambia={setScelti} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={riservato} onChange={(e) => setRiservato(e.target.checked)} />
            <span>Riservato — visibile solo a direzione e amministrazione</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Apertura..." : "Apri rilievo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Un passo del trattamento. Sta fuori dal dialogo di proposito: un componente
// definito dentro un altro viene ricreato a ogni battuta di tasto, e il cursore
// salta fuori dalle caselle di testo.
function Passo({ n, titolo, fatto, children }) {
  return (
    <div className="border-l-2 pl-3 py-1"
         style={{ borderColor: fatto ? "#059669" : "#e2e8f0" }}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${
          fatto ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
          {fatto ? "✓" : n}
        </span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{titolo}</span>
      </div>
      {children}
    </div>
  );
}

function DialogNonConformita({ nc, sistemi, puoScrivere, onChiudi, onCambio }) {
  const oggi = new Date().toISOString().substring(0, 10);
  const [d, setD] = useState(nc);
  const [correzione, setCorrezione] = useState(nc.correzione || "");
  const [causa, setCausa] = useState(nc.causa || "");
  const [azione, setAzione] = useState(nc.azione_correttiva || "");
  const [responsabile, setResponsabile] = useState(nc.responsabile || "");
  const [scadenza, setScadenza] = useState((nc.scadenza || "").substring(0, 10));
  const [busy, setBusy] = useState(false);

  const chiusa = d.stato === "chiusa" || d.stato === "annullata";
  const s = STATI_NC[d.stato] || STATI_NC.aperta;

  const chiama = async (fn, messaggio) => {
    setBusy(true);
    try {
      const { data } = await fn();
      setD(data);
      toast.success(messaggio);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      return null;
    } finally { setBusy(false); }
  };

  const salva = () => chiama(
    () => api.put(`/sgi/nc/${d.id}`, {
      correzione: correzione || null, causa: causa || null,
      azione_correttiva: azione || null, responsabile: responsabile || null,
      scadenza: scadenza || null,
    }),
    "Trattamento salvato");

  const attuata = () => chiama(
    () => api.post(`/sgi/nc/${d.id}/attuata`, { data: oggi }),
    "Azione registrata come attuata");

  const verifica = (esito) => chiama(
    () => api.post(`/sgi/nc/${d.id}/verifica`, { data: oggi, esito }),
    esito === "efficace" ? "Verifica positiva: rilievo chiuso"
                         : "Verifica negativa: il rilievo torna in lavorazione");

  const riapri = () => chiama(() => api.post(`/sgi/nc/${d.id}/riapri`), "Rilievo riaperto");

  const annulla = async () => {
    if (!window.confirm(
      `Annullare il rilievo ${d.numero}?\n\n` +
      `Resta in archivio ma esce dai conteggi. Si usa per i rilievi aperti per sbaglio.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/sgi/nc/${d.id}/annulla`);
      toast.success("Rilievo annullato");
      onCambio();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => onChiudi()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm">{d.numero}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.classe}`}>
              {s.nome}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <div className="text-sm">{d.descrizione}</div>
            <div className="text-xs text-slate-500 mt-1">
              {d.tipo_nome} · {d.origine_nome_norma}
              {d.origine_nome ? ` · ${d.origine_nome}` : ""}
              {" · rilevata il "}{dataIt(d.rilevata_il)}
              {d.rilevata_da ? ` da ${d.rilevata_da}` : ""}
            </div>
            <div className="mt-2"><Tessere sistemi={sistemi} codici={d.sistemi} compatte /></div>
          </div>

          <Passo n="1" titolo="Correzione immediata" fatto={Boolean(d.correzione)}>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[52px]"
                      value={correzione} onChange={(e) => setCorrezione(e.target.value)}
                      disabled={chiusa || !puoScrivere}
                      placeholder="Cosa si è fatto subito per rimediare" />
          </Passo>

          <Passo n="2" titolo="Analisi della causa" fatto={Boolean(d.causa)}>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[52px]"
                      value={causa} onChange={(e) => setCausa(e.target.value)}
                      disabled={chiusa || !puoScrivere}
                      placeholder="Perché è successo" />
          </Passo>

          <Passo n="3" titolo="Azione correttiva" fatto={Boolean(d.azione_correttiva)}>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[52px]"
                      value={azione} onChange={(e) => setAzione(e.target.value)}
                      disabled={chiusa || !puoScrivere}
                      placeholder="Cosa si fa perché non succeda più" />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><Label className="text-xs">Responsabile</Label>
                <Input value={responsabile} onChange={(e) => setResponsabile(e.target.value)}
                       disabled={chiusa || !puoScrivere} placeholder="Chi la fa" /></div>
              <div><Label className="text-xs">Entro il</Label>
                <Input type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)}
                       disabled={chiusa || !puoScrivere} /></div>
            </div>
          </Passo>

          <Passo n="4" titolo="Attuazione" fatto={Boolean(d.attuata_il)}>
            {d.attuata_il ? (
              <p className="text-sm text-slate-600">
                Attuata il <span className="font-medium">{dataIt(d.attuata_il)}</span>
                {d.attuazione_note ? ` · ${d.attuazione_note}` : ""}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Da registrare quando l&apos;azione è stata effettivamente fatta.
              </p>
            )}
          </Passo>

          <Passo n="5" titolo="Verifica di efficacia" fatto={d.verifica_esito === "efficace"}>
            {d.verifica_data ? (
              <p className={`text-sm font-medium ${d.verifica_esito === "efficace" ? "text-emerald-700" : "text-red-600"}`}>
                {d.verifica_esito === "efficace" ? "Efficace" : "Non efficace"} · {dataIt(d.verifica_data)}
                {d.verifica_note ? <span className="text-slate-600 font-normal"> — {d.verifica_note}</span> : null}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Si verifica dopo l&apos;attuazione: se l&apos;azione non ha funzionato, il rilievo
                torna in lavorazione invece di chiudersi.
              </p>
            )}
          </Passo>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {puoScrivere && !chiusa && (
            <Button variant="outline" onClick={salva} disabled={busy}>Salva</Button>
          )}
          {puoScrivere && !chiusa && d.azione_correttiva && !d.attuata_il && (
            <Button onClick={attuata} disabled={busy} className="bg-[#0067b2] hover:bg-[#00568f] text-white">
              <Check className="w-3 h-3 mr-1" /> Azione attuata
            </Button>
          )}
          {puoScrivere && d.stato === "da_verificare" && (
            <>
              <Button variant="outline" onClick={() => verifica("non_efficace")} disabled={busy}
                      className="text-red-600">
                <XCircle className="w-3 h-3 mr-1" /> Non efficace
              </Button>
              <Button onClick={() => verifica("efficace")} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Efficace, chiudi
              </Button>
            </>
          )}
          {puoScrivere && d.stato === "chiusa" && (
            <Button variant="outline" onClick={riapri} disabled={busy}>
              <Undo2 className="w-3 h-3 mr-1" /> Riapri
            </Button>
          )}
          {puoScrivere && !chiusa && (
            <Button variant="outline" onClick={annulla} disabled={busy} className="text-red-600">
              Annulla rilievo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Audit
// -----------------------------------------------------------------------------
function Audit({ sistemi, riferimenti, puoScrivere, sistemaSel, setSistemaSel }) {
  const [righe, setRighe] = useState([]);
  const [carico, setCarico] = useState(true);
  const [tipo, setTipo] = useState("");
  const [nuovo, setNuovo] = useState(false);
  const [modifica, setModifica] = useState(null);
  const [rilievi, setRilievi] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (sistemaSel) p.set("sistema", sistemaSel);
      if (tipo) p.set("tipo", tipo);
      const { data } = await api.get(`/sgi/audit?${p.toString()}`);
      setRighe(data);
    } catch { toast.error("Errore nel caricamento degli audit"); }
    finally { setCarico(false); }
  }, [sistemaSel, tipo]);

  useEffect(() => { carica(); }, [carica]);

  const elimina = async (a) => {
    if (!window.confirm(`Eliminare "${a.titolo}"?\n\nViene cancellato anche il rapporto allegato.`)) return;
    try {
      await api.delete(`/sgi/audit/${a.id}`);
      toast.success("Audit eliminato");
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {(riferimenti?.tipi_audit || []).map((t) => (
            <option key={t.codice} value={t.codice}>{t.nome}</option>
          ))}
        </select>
        <span className="text-xs text-slate-400 ml-auto">{righe.length} audit</span>
        {puoScrivere && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuovo(true)} data-testid="nuovo-audit">
            <Plus className="w-3 h-3 mr-1" /> Nuovo audit
          </Button>
        )}
      </div>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && righe.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500 space-y-2">
          <p>Nessun audit registrato.</p>
          <p className="text-xs">
            Mettendo in calendario gli audit dell&apos;anno, compaiono nello scadenzario
            al posto del promemoria generico del mese.
          </p>
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {righe.map((a) => {
          const st = STATI_AUDIT_UI[a.stato] || STATI_AUDIT_UI.pianificato;
          const quando = a.data_svolgimento || a.data_pianificata;
          return (
            <Card key={a.id} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.classe}`}>
                        {st.nome}
                      </span>
                      <Badge variant="outline" className="text-[10px]">{a.tipo_nome}</Badge>
                    </div>
                    <div className="font-semibold text-sm">{a.titolo}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {[a.ente, a.auditor, a.aree].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {a.esito && <p className="text-xs text-slate-600 mt-1">{a.esito}</p>}
                  </div>

                  <div className="min-w-[120px]">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                      {a.data_svolgimento ? "Svolto il" : "Previsto per"}
                    </div>
                    <div className="text-sm font-semibold tabular-nums">{dataIt(quando)}</div>
                  </div>

                  <div className="text-center min-w-[70px]">
                    <div className={`font-display text-xl font-black ${a.aperti ? "text-amber-600" : a.rilievi ? "text-slate-800" : "text-slate-300"}`}>
                      {a.rilievi || 0}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                      rilievi{a.aperti ? ` · ${a.aperti} aperti` : ""}
                    </div>
                  </div>

                  <Tessere sistemi={sistemi} codici={a.sistemi} compatte />

                  <div className="flex gap-1">
                    {a.rilievi > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setRilievi(a)} title="Rilievi">
                        <AlertTriangle className="w-3 h-3" />
                      </Button>
                    )}
                    {a.file_url && (
                      <a href={fileUrl(a.file_url)} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" title="Rapporto">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </a>
                    )}
                    {puoScrivere && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setModifica(a)} title="Modifica">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => elimina(a)}
                                className="text-red-600" title="Elimina">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(nuovo || modifica) && (
        <DialogAudit audit={modifica} sistemi={sistemi} riferimenti={riferimenti}
                     onChiudi={() => { setNuovo(false); setModifica(null); }}
                     onSalvato={() => { setNuovo(false); setModifica(null); carica(); }} />
      )}
      {rilievi && <DialogRilievi audit={rilievi} onChiudi={() => setRilievi(null)} />}
    </div>
  );
}

function DialogAudit({ audit, sistemi, riferimenti, onChiudi, onSalvato }) {
  const modifica = Boolean(audit);
  const [titolo, setTitolo] = useState(audit?.titolo || "");
  const [tipo, setTipo] = useState(audit?.tipo || "interno");
  const [scelti, setScelti] = useState(audit?.sistemi || []);
  const [pianificata, setPianificata] = useState((audit?.data_pianificata || "").substring(0, 10));
  const [svolgimento, setSvolgimento] = useState((audit?.data_svolgimento || "").substring(0, 10));
  const [auditor, setAuditor] = useState(audit?.auditor || "");
  const [ente, setEnte] = useState(audit?.ente || "");
  const [aree, setAree] = useState(audit?.aree || "");
  const [esito, setEsito] = useState(audit?.esito || "");
  const [file, setFile] = useState(null);
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    if (!titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (scelti.length === 0) { toast.error("Indica almeno un sistema"); return; }
    if (!pianificata && !svolgimento) {
      toast.error("Serve almeno una data, prevista o di svolgimento"); return;
    }
    setSalvo(true);
    try {
      if (modifica) {
        await api.put(`/sgi/audit/${audit.id}`, {
          titolo: titolo.trim(), tipo, sistemi: scelti,
          data_pianificata: pianificata || null,
          data_svolgimento: svolgimento || null,
          auditor: auditor || null, ente: ente || null,
          aree: aree || null, esito: esito || null,
        });
        toast.success("Audit aggiornato");
      } else {
        const fd = new FormData();
        fd.append("titolo", titolo.trim());
        fd.append("tipo", tipo);
        fd.append("sistemi", scelti.join(","));
        if (pianificata) fd.append("data_pianificata", pianificata);
        if (svolgimento) fd.append("data_svolgimento", svolgimento);
        if (auditor) fd.append("auditor", auditor);
        if (ente) fd.append("ente", ente);
        if (aree) fd.append("aree", aree);
        if (esito) fd.append("esito", esito);
        if (file) fd.append("file", file);
        await api.post("/sgi/audit", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Audit registrato");
      }
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modifica ? "Modifica audit" : "Nuovo audit"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Titolo *</Label>
            <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                   placeholder="es. Sorveglianza ISO 37001 e 39001" /></div>
          <div><Label>Tipo</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={tipo}
                    onChange={(e) => setTipo(e.target.value)}>
              {(riferimenti?.tipi_audit || []).map((t) => (
                <option key={t.codice} value={t.codice}>{t.nome}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data prevista</Label>
              <Input type="date" value={pianificata} onChange={(e) => setPianificata(e.target.value)} /></div>
            <div><Label>Data di svolgimento</Label>
              <Input type="date" value={svolgimento} onChange={(e) => setSvolgimento(e.target.value)} /></div>
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            Compilando la data di svolgimento l&apos;audit passa da solo a &quot;svolto&quot; e
            sparisce dallo scadenzario.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ente</Label>
              <Input value={ente} onChange={(e) => setEnte(e.target.value)} placeholder="es. RINA" /></div>
            <div><Label>Auditor</Label>
              <Input value={auditor} onChange={(e) => setAuditor(e.target.value)} /></div>
          </div>
          <div><Label>Aree e processi esaminati</Label>
            <Input value={aree} onChange={(e) => setAree(e.target.value)}
                   placeholder="es. Cantieri, magazzino, formazione" /></div>
          <div><Label>Esito</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[52px]"
                      value={esito} onChange={(e) => setEsito(e.target.value)}
                      placeholder="Sintesi delle risultanze" /></div>
          <div><Label>Sistemi esaminati *</Label>
            <div className="mt-1"><SceltaSistemi sistemi={sistemi} scelti={scelti} onCambia={setScelti} /></div>
          </div>
          {modifica ? (
            <p className="text-xs text-slate-500">
              Il rapporto allegato resta quello caricato{audit.filename ? `: ${audit.filename}` : ""}.
            </p>
          ) : (
            <div><Label>Rapporto di audit</Label>
              <Input type="file" accept=".pdf,.doc,.docx,image/*"
                     onChange={(e) => setFile(e.target.files[0])} /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : modifica ? "Salva modifiche" : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogRilievi({ audit, onChiudi }) {
  const [righe, setRighe] = useState(null);
  useEffect(() => {
    api.get(`/sgi/audit/${audit.id}/rilievi`)
      .then(({ data }) => setRighe(data))
      .catch(() => setRighe([]));
  }, [audit.id]);

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Rilievi · {audit.titolo}</DialogTitle></DialogHeader>
        {righe === null && <p className="text-sm text-slate-500 py-4">Caricamento...</p>}
        {righe && righe.length === 0 && (
          <p className="text-sm text-slate-500 py-4">Nessun rilievo collegato a questo audit.</p>
        )}
        {righe && righe.length > 0 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {righe.map((n) => {
              const s = STATI_NC[n.stato] || STATI_NC.aperta;
              return (
                <div key={n.id} className="border border-slate-200 rounded p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs font-semibold text-slate-500">{n.numero}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.classe}`}>
                      {s.nome}
                    </span>
                  </div>
                  <div className="text-sm mt-1">{n.descrizione}</div>
                  {n.scadenza && (
                    <div className={`text-xs mt-1 ${n.in_ritardo ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                      Entro il {dataIt(n.scadenza)}
                      {n.responsabile ? ` · ${n.responsabile}` : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onChiudi}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Obiettivi e piani di miglioramento
// -----------------------------------------------------------------------------
function Obiettivi({ sistemi, puoScrivere, sistemaSel, setSistemaSel, onCambio }) {
  const annoCorrente = new Date().getFullYear();
  const [anno, setAnno] = useState(annoCorrente);
  const [righe, setRighe] = useState([]);
  const [carico, setCarico] = useState(true);
  const [nuovo, setNuovo] = useState(false);
  const [modifica, setModifica] = useState(null);
  const [avanza, setAvanza] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams({ anno: String(anno) });
      if (sistemaSel) p.set("sistema", sistemaSel);
      const { data } = await api.get(`/sgi/obiettivi?${p.toString()}`);
      setRighe(data);
    } catch { toast.error("Errore nel caricamento degli obiettivi"); }
    finally { setCarico(false); }
  }, [anno, sistemaSel]);

  useEffect(() => { carica(); }, [carica]);

  const elimina = async (o) => {
    if (!window.confirm(`Eliminare l'obiettivo "${o.titolo}"?\n\nSi perde anche lo storico dei valori.`)) return;
    try {
      await api.delete(`/sgi/obiettivi/${o.id}`);
      toast.success("Obiettivo eliminato");
      carica(); onCambio();
    } catch { toast.error("Errore"); }
  };

  const chiudi = async (o, stato) => {
    try {
      await api.put(`/sgi/obiettivi/${o.id}`, { stato });
      toast.success(stato === "raggiunto" ? "Obiettivo raggiunto" : "Obiettivo chiuso");
      carica(); onCambio();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        {[annoCorrente - 1, annoCorrente, annoCorrente + 1].map((a) => (
          <button key={a} type="button" onClick={() => setAnno(a)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border ${
                    anno === a ? "bg-[#0067b2] text-white border-[#0067b2]"
                               : "bg-white text-slate-600 border-slate-200"}`}>
            {a}
          </button>
        ))}
        <span className="w-px h-6 bg-slate-200 mx-1" />
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
        <span className="text-xs text-slate-400 ml-auto">{righe.length} obiettivi</span>
        {puoScrivere && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuovo(true)} data-testid="nuovo-obiettivo">
            <Plus className="w-3 h-3 mr-1" /> Nuovo obiettivo
          </Button>
        )}
      </div>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && righe.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500 space-y-2">
          <p>Nessun obiettivo per il {anno}.</p>
          <p className="text-xs">
            Sono gli obiettivi di miglioramento che le norme chiedono ogni anno,
            con l&apos;indicatore che li misura.
          </p>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {righe.map((o) => {
          const st = STATI_OBIETTIVO_UI[o.stato] || STATI_OBIETTIVO_UI.aperto;
          const perc = o.avanzamento;
          const colore = perc === null ? "#cbd5e1"
            : perc >= 100 ? "#059669" : perc >= 50 ? "#0067b2" : "#f59e0b";
          return (
            <Card key={o.id} className={o.in_ritardo ? "border-red-200 bg-red-50/50" : "border-slate-200"}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{o.titolo}</div>
                    {o.indicatore && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        {o.indicatore}{o.unita ? ` (${o.unita})` : ""}
                      </div>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.classe}`}>
                    {st.nome}
                  </span>
                </div>

                {o.descrizione && <p className="text-xs text-slate-500">{o.descrizione}</p>}

                {perc !== null && (
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-500 tabular-nums">
                        da {o.valore_partenza} a {o.valore_obiettivo}
                        {o.valore_attuale !== null ? ` · ora ${o.valore_attuale}` : ""}
                      </span>
                      <span className="font-semibold tabular-nums" style={{ color: colore }}>
                        {perc}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                           style={{ width: `${Math.min(perc, 100)}%`, background: colore }} />
                    </div>
                  </div>
                )}

                <Tessere sistemi={sistemi} codici={o.sistemi} compatte />

                <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                  <div className="text-xs text-slate-500">
                    {o.responsabile}
                    {o.scadenza ? ` · entro il ${dataIt(o.scadenza)}` : ""}
                    {o.in_ritardo && <span className="text-red-600 font-semibold"> · scaduto</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setAvanza(o)}>
                      <Flag className="w-3 h-3 mr-1" /> Aggiorna
                    </Button>
                    {puoScrivere && o.stato === "aperto" && (
                      <Button size="sm" variant="outline" onClick={() => chiudi(o, "raggiunto")}
                              className="text-emerald-700" title="Segna raggiunto">
                        <CheckCircle2 className="w-3 h-3" />
                      </Button>
                    )}
                    {puoScrivere && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setModifica(o)} title="Modifica">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => elimina(o)}
                                className="text-red-600" title="Elimina">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(nuovo || modifica) && (
        <DialogObiettivo obiettivo={modifica} anno={anno} sistemi={sistemi}
                         onChiudi={() => { setNuovo(false); setModifica(null); }}
                         onSalvato={() => { setNuovo(false); setModifica(null); carica(); onCambio(); }} />
      )}
      {avanza && (
        <DialogAvanzamento obiettivo={avanza} puoScrivere={puoScrivere}
                           onChiudi={() => setAvanza(null)}
                           onSalvato={() => { setAvanza(null); carica(); }} />
      )}
    </div>
  );
}

function DialogObiettivo({ obiettivo, anno, sistemi, onChiudi, onSalvato }) {
  const modifica = Boolean(obiettivo);
  const [titolo, setTitolo] = useState(obiettivo?.titolo || "");
  const [descrizione, setDescrizione] = useState(obiettivo?.descrizione || "");
  const [indicatore, setIndicatore] = useState(obiettivo?.indicatore || "");
  const [unita, setUnita] = useState(obiettivo?.unita || "");
  const [partenza, setPartenza] = useState(
    obiettivo?.valore_partenza !== null && obiettivo?.valore_partenza !== undefined
      ? String(obiettivo.valore_partenza) : "");
  const [traguardo, setTraguardo] = useState(
    obiettivo?.valore_obiettivo !== null && obiettivo?.valore_obiettivo !== undefined
      ? String(obiettivo.valore_obiettivo) : "");
  const [responsabile, setResponsabile] = useState(obiettivo?.responsabile || "Direzione");
  const [scadenza, setScadenza] = useState(
    (obiettivo?.scadenza || `${anno}-12-31`).substring(0, 10));
  const [scelti, setScelti] = useState(obiettivo?.sistemi || []);
  const [salvo, setSalvo] = useState(false);

  const numero = (v) => (v === "" || v === null ? null : Number(v));

  const salva = async () => {
    if (!titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (scelti.length === 0) { toast.error("Indica almeno un sistema"); return; }
    if (numero(partenza) !== null && numero(traguardo) !== null
        && numero(partenza) === numero(traguardo)) {
      toast.error("Partenza e traguardo non possono coincidere"); return;
    }
    setSalvo(true);
    const corpo = {
      titolo: titolo.trim(), descrizione: descrizione || null,
      sistemi: scelti, indicatore: indicatore || null, unita: unita || null,
      valore_partenza: numero(partenza), valore_obiettivo: numero(traguardo),
      responsabile, scadenza: scadenza || null,
    };
    try {
      if (modifica) {
        await api.put(`/sgi/obiettivi/${obiettivo.id}`, corpo);
        toast.success("Obiettivo aggiornato");
      } else {
        await api.post("/sgi/obiettivi", { ...corpo, anno });
        toast.success("Obiettivo creato");
      }
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modifica ? "Modifica obiettivo" : `Nuovo obiettivo ${anno}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Titolo *</Label>
            <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                   placeholder="es. Ridurre del 30% i near miss" /></div>
          <div><Label>Descrizione</Label>
            <Input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Indicatore</Label>
              <Input value={indicatore} onChange={(e) => setIndicatore(e.target.value)}
                     placeholder="es. Near miss all'anno" /></div>
            <div><Label>Unità</Label>
              <Input value={unita} onChange={(e) => setUnita(e.target.value)} placeholder="n. / % / ore" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Valore di partenza</Label>
              <Input type="number" value={partenza} onChange={(e) => setPartenza(e.target.value)} /></div>
            <div><Label>Traguardo</Label>
              <Input type="number" value={traguardo} onChange={(e) => setTraguardo(e.target.value)} /></div>
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            Funziona anche al ribasso: se il traguardo è più basso della partenza,
            l&apos;avanzamento conta comunque la strada fatta.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Responsabile</Label>
              <Input value={responsabile} onChange={(e) => setResponsabile(e.target.value)} /></div>
            <div><Label>Entro il</Label>
              <Input type="date" value={scadenza} onChange={(e) => setScadenza(e.target.value)} /></div>
          </div>
          <div><Label>Sistemi *</Label>
            <div className="mt-1"><SceltaSistemi sistemi={sistemi} scelti={scelti} onCambia={setScelti} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : modifica ? "Salva modifiche" : "Crea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogAvanzamento({ obiettivo, puoScrivere, onChiudi, onSalvato }) {
  const oggi = new Date().toISOString().substring(0, 10);
  const [valore, setValore] = useState("");
  const [data, setData] = useState(oggi);
  const [note, setNote] = useState("");
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    if (valore === "") { toast.error("Indica il valore misurato"); return; }
    setSalvo(true);
    try {
      await api.post(`/sgi/obiettivi/${obiettivo.id}/avanzamento`, {
        valore: Number(valore), data, note: note || null,
      });
      toast.success("Avanzamento registrato");
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  const storico = [...(obiettivo.storico || [])].reverse();

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>{obiettivo.titolo}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {puoScrivere && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valore misurato *</Label>
                <Input type="number" value={valore} onChange={(e) => setValore(e.target.value)}
                       placeholder={obiettivo.unita || ""} /></div>
              <div><Label>Alla data</Label>
                <Input type="date" value={data} onChange={(e) => setData(e.target.value)} /></div>
            </div>
          )}
          {puoScrivere && (
            <div><Label>Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
          )}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
              Storico delle misure
            </div>
            {storico.length === 0 && (
              <p className="text-sm text-slate-500">Nessuna misura registrata finora.</p>
            )}
            <div className="space-y-1 max-h-[32vh] overflow-y-auto">
              {storico.map((r, i) => (
                <div key={`${r.data}-${i}`}
                     className="flex items-center gap-3 border border-slate-200 rounded px-3 py-2">
                  <span className="text-xs text-slate-500 tabular-nums w-20">{dataIt(r.data)}</span>
                  <span className="font-semibold tabular-nums">
                    {r.valore}{obiettivo.unita ? ` ${obiettivo.unita}` : ""}
                  </span>
                  {r.note && <span className="text-xs text-slate-500 truncate">{r.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {puoScrivere && (
            <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
              {salvo ? "Salvataggio..." : "Registra"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Riesame della direzione
// -----------------------------------------------------------------------------
function Riesame({ sistemi, puoScrivere }) {
  const annoCorrente = new Date().getFullYear();
  const [anno, setAnno] = useState(annoCorrente);
  const [dati, setDati] = useState(null);
  const [riesami, setRiesami] = useState([]);
  const [carico, setCarico] = useState(true);
  const [apri, setApri] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const [d, r] = await Promise.all([
        api.get(`/sgi/riesame/dati?anno=${anno}`),
        api.get("/sgi/riesami"),
      ]);
      setDati(d.data);
      setRiesami(r.data);
    } catch { toast.error("Errore nel caricamento del riesame"); }
    finally { setCarico(false); }
  }, [anno]);

  useEffect(() => { carica(); }, [carica]);

  const mio = riesami.find((r) => r.anno === anno);

  const crea = async () => {
    try {
      await api.post("/sgi/riesami", { anno, sistemi: sistemi.map((s) => s.codice) });
      toast.success(`Riesame ${anno} aperto in bozza`);
      carica();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        {[annoCorrente - 2, annoCorrente - 1, annoCorrente].map((a) => (
          <button key={a} type="button" onClick={() => setAnno(a)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border ${
                    anno === a ? "bg-[#0067b2] text-white border-[#0067b2]"
                               : "bg-white text-slate-600 border-slate-200"}`}>
            {a}
          </button>
        ))}
        <div className="ml-auto flex gap-2 items-center">
          {mio ? (
            <>
              <span className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${
                mio.stato === "approvato"
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                verbale {mio.stato}
              </span>
              <Button size="sm" variant="outline" onClick={() => setApri(mio)}>
                Apri verbale
              </Button>
            </>
          ) : puoScrivere && (
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={crea}
                    data-testid="nuovo-riesame">
              <Plus className="w-3 h-3 mr-1" /> Apri il riesame {anno}
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Questi sono gli input previsti dalle norme, calcolati sui dati dell&apos;anno.
        Non si compilano a mano: alla direzione restano la valutazione e le decisioni.
      </p>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!carico && dati && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <BloccoRiesame titolo="Non conformità" icona={AlertTriangle} voci={[
            ["Aperte nell'anno", dati.non_conformita.aperte_nell_anno],
            ["Chiuse nell'anno", dati.non_conformita.chiuse_nell_anno],
            ["Ancora aperte", dati.non_conformita.ancora_aperte],
            ["Oltre la scadenza", dati.non_conformita.in_ritardo, dati.non_conformita.in_ritardo > 0],
            ["Giorni medi di chiusura", dati.non_conformita.giorni_medi_chiusura ?? "—"],
          ]} extra={Object.entries(dati.non_conformita.per_origine || {})} />

          <BloccoRiesame titolo="Audit" icona={FileSignature} voci={[
            ["Svolti nell'anno", dati.audit.svolti_nell_anno],
            ["Di cui interni", dati.audit.interni],
            ["Di cui dell'ente", dati.audit.ente],
            ["Pianificati non svolti", dati.audit.pianificati_non_svolti,
             dati.audit.pianificati_non_svolti > 0],
          ]} />

          <BloccoRiesame titolo="Obiettivi" icona={Flag} voci={[
            ["Totale dell'anno", dati.obiettivi.totale],
            ["Raggiunti", dati.obiettivi.raggiunti],
            ["Non raggiunti", dati.obiettivi.non_raggiunti, dati.obiettivi.non_raggiunti > 0],
            ["Ancora aperti", dati.obiettivi.aperti],
          ]} />

          <BloccoRiesame titolo="Controlli" icona={ClipboardList} voci={[
            ["Schede attive", dati.controlli.attivi],
            ["Eseguiti nell'anno", dati.controlli.eseguiti_nell_anno],
            ["Con esito non conforme", dati.controlli.esiti_non_conformi,
             dati.controlli.esiti_non_conformi > 0],
            ["Scaduti oggi", dati.controlli.scaduti_oggi, dati.controlli.scaduti_oggi > 0],
            ["Mai eseguiti", dati.controlli.mai_eseguiti],
          ]} />

          <BloccoRiesame titolo="Persone e documenti" icona={FileText} voci={[
            ["Attestati conseguiti", dati.persone.attestati_conseguiti],
            ["In scadenza a 90 giorni", dati.persone.attestati_in_scadenza_90gg],
            ["Consegne DPI", dati.persone.consegne_dpi],
            ["Documenti in vigore", dati.documentazione.vigenti],
            ["Revisionati nell'anno", dati.documentazione.revisionati_nell_anno],
          ]} />

          <BloccoRiesame titolo="Dal campo" icona={ShieldCheck} voci={[
            ["Segnalazioni", dati.campo.segnalazioni],
            ["Verifiche in cantiere", dati.campo.verifiche_cantiere],
            ["Manutenzioni registrate", dati.campo.manutenzioni_registrate],
          ]} extra={Object.entries(dati.campo.per_gravita || {})} />
        </div>
      )}

      {apri && (
        <DialogRiesame riesame={apri} onChiudi={() => setApri(null)}
                       puoScrivere={puoScrivere}
                       onCambio={() => { setApri(null); carica(); }} />
      )}
    </div>
  );
}

function BloccoRiesame({ titolo, icona: Icona, voci, extra }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icona className="w-4 h-4 text-[#0067b2]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{titolo}</span>
        </div>
        <div className="space-y-1.5">
          {voci.map(([nome, valore, allarme]) => (
            <div key={nome} className="flex justify-between items-baseline gap-3">
              <span className="text-sm text-slate-600">{nome}</span>
              <span className={`font-semibold tabular-nums ${allarme ? "text-red-600" : "text-slate-800"}`}>
                {valore}
              </span>
            </div>
          ))}
        </div>
        {extra && extra.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
            {extra.map(([nome, valore]) => (
              <div key={nome} className="flex justify-between items-baseline gap-3">
                <span className="text-xs text-slate-500">{nome}</span>
                <span className="text-xs font-medium tabular-nums text-slate-600">{valore}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DialogRiesame({ riesame, puoScrivere, onChiudi, onCambio }) {
  const [r, setR] = useState(riesame);
  const [data, setData] = useState((riesame.data || "").substring(0, 10));
  const [partecipanti, setPartecipanti] = useState(riesame.partecipanti || "");
  const [valutazione, setValutazione] = useState(riesame.valutazione || "");
  const [decisioni, setDecisioni] = useState(riesame.decisioni || "");
  const [risorse, setRisorse] = useState(riesame.risorse || "");
  const [busy, setBusy] = useState(false);

  const approvato = r.stato === "approvato";

  const salva = async () => {
    setBusy(true);
    try {
      const { data: agg } = await api.put(`/sgi/riesami/${r.id}`, {
        data: data || null, partecipanti: partecipanti || null,
        valutazione: valutazione || null, decisioni: decisioni || null,
        risorse: risorse || null,
      });
      setR(agg);
      toast.success("Verbale salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const approva = async () => {
    if (!window.confirm(
      "Approvare il verbale?\n\n" +
      "I numeri dell'anno vengono congelati come sono adesso: il verbale non " +
      "cambierà più anche se i dati continuano a muoversi."
    )) return;
    setBusy(true);
    try {
      await api.put(`/sgi/riesami/${r.id}`, {
        data: data || null, partecipanti: partecipanti || null,
        valutazione: valutazione || null, decisioni: decisioni || null,
        risorse: risorse || null,
      });
      const { data: agg } = await api.post(`/sgi/riesami/${r.id}/approva`);
      setR(agg);
      toast.success("Verbale approvato e numeri congelati");
      onCambio();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const riapri = async () => {
    setBusy(true);
    try {
      const { data: agg } = await api.post(`/sgi/riesami/${r.id}/riapri`);
      setR(agg);
      toast.success("Verbale riaperto");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Riesame della direzione {r.anno}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {approvato && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800">
              Verbale approvato: i numeri dell&apos;anno sono congelati come erano
              al momento dell&apos;approvazione.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data della riunione</Label>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)}
                     disabled={approvato || !puoScrivere} /></div>
            <div><Label>Partecipanti</Label>
              <Input value={partecipanti} onChange={(e) => setPartecipanti(e.target.value)}
                     disabled={approvato || !puoScrivere}
                     placeholder="Direzione, RSGI, RSPP..." /></div>
          </div>
          <div><Label>Valutazione dell&apos;adeguatezza del sistema</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[70px]"
                      value={valutazione} onChange={(e) => setValutazione(e.target.value)}
                      disabled={approvato || !puoScrivere}
                      placeholder="Il sistema è ancora adeguato, efficace e allineato al contesto?" /></div>
          <div><Label>Decisioni della direzione *</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[70px]"
                      value={decisioni} onChange={(e) => setDecisioni(e.target.value)}
                      disabled={approvato || !puoScrivere}
                      placeholder="Cosa si è deciso di fare" /></div>
          <div><Label>Risorse necessarie</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[52px]"
                      value={risorse} onChange={(e) => setRisorse(e.target.value)}
                      disabled={approvato || !puoScrivere}
                      placeholder="Persone, formazione, attrezzature, investimenti" /></div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {puoScrivere && !approvato && (
            <>
              <Button variant="outline" onClick={salva} disabled={busy}>Salva bozza</Button>
              <Button onClick={approva} disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Approva
              </Button>
            </>
          )}
          {puoScrivere && approvato && (
            <Button variant="outline" onClick={riapri} disabled={busy}>
              <Undo2 className="w-3 h-3 mr-1" /> Riapri
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Il pacchetto per l'audit
// -----------------------------------------------------------------------------
const ESITI_PRONTEZZA = {
  manca:      { nome: "Manca",       classe: "bg-red-100 text-red-700 border-red-200",       punto: "#dc2626" },
  attenzione: { nome: "Da guardare", classe: "bg-amber-100 text-amber-800 border-amber-200", punto: "#d97706" },
  ok:         { nome: "A posto",     classe: "bg-emerald-100 text-emerald-800 border-emerald-200", punto: "#059669" },
};

function Fascicolo({ sistemi, sistemaSel, setSistemaSel, puoScrivere }) {
  const [importaEvidenze, setImportaEvidenze] = useState(false);
  // Senza una norma scelta non c'e' fascicolo: si parte da quella con l'audit
  // piu' vicino, che e' la prima dell'elenco.
  const scelto = sistemaSel || (sistemi[0] || {}).codice || "";
  const oggi = new Date().toISOString().substring(0, 10);
  const unAnnoFa = new Date(Date.now() - 365 * 86400000).toISOString().substring(0, 10);
  const [dal, setDal] = useState(unAnnoFa);
  const [al, setAl] = useState(oggi);
  const [pron, setPron] = useState(null);
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [scarico, setScarico] = useState(null);

  const carica = useCallback(async () => {
    if (!scelto) return;
    setCarico(true);
    try {
      const q = `sistema=${scelto}&dal=${dal}&al=${al}`;
      const [p, f] = await Promise.all([
        api.get(`/sgi/prontezza?sistema=${scelto}`),
        api.get(`/sgi/fascicolo?${q}`),
      ]);
      setPron(p.data);
      setDati(f.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento del fascicolo");
    } finally { setCarico(false); }
  }, [scelto, dal, al]);

  useEffect(() => { carica(); }, [carica]);

  // Il browser non puo' scaricare da solo un endpoint protetto: si prende il
  // file con l'axios che porta il token e lo si consegna come blob.
  const scarica = async (formato) => {
    setScarico(formato);
    try {
      const { data, headers } = await api.get(
        `/sgi/fascicolo/${formato}?sistema=${scelto}&dal=${dal}&al=${al}`,
        { responseType: "blob" });
      const proposto = (headers["content-disposition"] || "").split("filename=")[1];
      const nome = proposto
        ? proposto.split('"').join("").trim()
        : `fascicolo_${scelto}.${formato === "excel" ? "xlsx" : "pdf"}`;
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Fascicolo scaricato");
    } catch {
      toast.error("Errore nella generazione del file");
    } finally { setScarico(null); }
  };

  const sistema = dati?.sistema;
  const capitoli = dati ? [
    { nome: "Documenti in vigore", n: dati.documenti.length },
    { nome: "Schede di controllo", n: dati.controlli.length },
    { nome: "Audit svolti", n: dati.audit.length },
    { nome: "Non conformità", n: dati.non_conformita.length },
    { nome: "Obiettivi", n: dati.obiettivi.length },
    { nome: "Riesami approvati", n: dati.riesami.length },
    { nome: "Attestati conseguiti", n: dati.formazione.length },
    ...dati.registri.map((r) => ({ nome: r.nome, n: r.righe.length })),
  ] : [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Scegli la norma e il periodo: esce il fascicolo che l&apos;ente si aspetta,
        preceduto dalla lista di quello che manca ancora.
      </p>

      <div className="flex gap-2 flex-wrap items-end">
        <div>
          <Label className="text-xs">Norma</Label>
          <select className="h-10 border border-slate-200 rounded px-3 text-sm bg-white block mt-1"
                  value={scelto} onChange={(e) => setSistemaSel(e.target.value)}>
            {sistemi.map((s) => (
              <option key={s.codice} value={s.codice}>{s.nome} — {s.oggetto}</option>
            ))}
          </select>
        </div>
        <div><Label className="text-xs">Dal</Label>
          <Input type="date" value={dal} onChange={(e) => setDal(e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs">Al</Label>
          <Input type="date" value={al} onChange={(e) => setAl(e.target.value)} className="mt-1" /></div>
        <div className="flex gap-2 ml-auto">
          {puoScrivere && (
            <Button variant="outline" onClick={() => setImportaEvidenze(true)} data-testid="importa-evidenze">
              <Upload className="w-4 h-4 mr-2" /> Importa evidenze
            </Button>
          )}
          <Button variant="outline" onClick={() => scarica("excel")}
                  disabled={Boolean(scarico) || !dati} data-testid="scarica-excel">
            {scarico === "excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                 : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Excel
          </Button>
          <Button onClick={() => scarica("pdf")} disabled={Boolean(scarico) || !dati}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="scarica-pdf">
            {scarico === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                               : <Download className="w-4 h-4 mr-2" />}
            Fascicolo PDF
          </Button>
        </div>
      </div>

      {importaEvidenze && (
        <DialogImportaEvidenze onChiudi={() => setImportaEvidenze(false)}
                               onFatto={() => { setImportaEvidenze(false); carica(); }} />
      )}

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!carico && pron && (
        <Card className={pron.pronto ? "border-emerald-200 bg-emerald-50/40"
                                     : pron.manca ? "border-red-200 bg-red-50/40"
                                                  : "border-amber-200 bg-amber-50/40"}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              {pron.pronto
                ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />}
              <div className="flex-1 min-w-[220px]">
                <div className="font-semibold text-sm">
                  {pron.pronto ? "Pronti per l'audit"
                    : pron.manca ? `${pron.manca} cose mancano prima dell'audit`
                                 : `${pron.attenzione} cose da guardare`}
                </div>
                <div className="text-xs text-slate-600">
                  Lista di riscontro su {sistema?.nome}
                  {dati?.prossimo_audit ? ` · audit a ${meseAnno(dati.prossimo_audit)}` : ""}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              {pron.voci.map((v) => {
                const e = ESITI_PRONTEZZA[v.esito] || ESITI_PRONTEZZA.ok;
                return (
                  <div key={v.nome}
                       className="flex items-start gap-2 bg-white border border-slate-200 rounded px-3 py-2">
                    <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                          style={{ background: e.punto }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{v.nome}</div>
                      <div className="text-xs text-slate-500">{v.dettaglio}</div>
                      {v.azione && v.esito !== "ok" && (
                        <div className="text-xs text-[#0067b2] mt-0.5">{v.azione}</div>
                      )}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${e.classe}`}>
                      {e.nome}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {!carico && dati && (
        <>
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold pt-2">
            Cosa contiene il fascicolo
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {capitoli.map((c) => (
              <Card key={c.nome} className="border-slate-200">
                <CardContent className="p-3">
                  <div className={`font-display text-2xl font-black tracking-tighter ${c.n ? "text-slate-800" : "text-slate-300"}`}>
                    {c.n}
                  </div>
                  <div className="text-xs text-slate-500 leading-tight mt-0.5">{c.nome}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-4 text-xs text-slate-500 space-y-1">
              <div>
                <span className="font-medium text-slate-700">{sistema?.nome}</span>
                {sistema?.ente ? ` · ${sistema.ente}` : ""}
                {sistema?.numero_certificato ? ` · certificato ${sistema.numero_certificato}` : ""}
                {sistema?.scadenza_certificato ? ` · scade il ${dataIt(sistema.scadenza_certificato)}` : ""}
              </div>
              {sistema?.certificato_documento && (
                <div>
                  Certificato preso da Documenti aziendali:{" "}
                  <a href={fileUrl(sistema.certificato_documento.file_url)} target="_blank"
                     rel="noopener noreferrer" className="text-[#0067b2] hover:underline">
                    {sistema.certificato_documento.title}
                  </a>
                  {" "}— compare fra i documenti in vigore del fascicolo.
                </div>
              )}
              <div>Periodo dal {dataIt(dati.dal)} al {dataIt(dati.al)}.</div>
              <div>
                Il PDF è quello da portare in riunione; l&apos;Excel ha un foglio per
                capitolo, con tutte le righe dei registri.
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// I registri di norma
// -----------------------------------------------------------------------------
function Registri({ sistemi, puoScrivere, sistemaSel, setSistemaSel, aperto, setAperto }) {
  const [elenco, setElenco] = useState([]);
  const [carico, setCarico] = useState(true);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const q = sistemaSel ? `?sistema=${sistemaSel}` : "";
      const { data } = await api.get(`/sgi/registri${q}`);
      setElenco(data);
    } catch { toast.error("Errore nel caricamento dei registri"); }
    finally { setCarico(false); }
  }, [sistemaSel]);

  useEffect(() => { carica(); }, [carica]);

  const registro = elenco.find((r) => r.codice === aperto);

  // Un registro aperto da un'altra scheda potrebbe non essere nell'elenco
  // filtrato: in quel caso si toglie il filtro invece di mostrare il vuoto.
  useEffect(() => {
    if (aperto && !carico && !registro && sistemaSel) setSistemaSel("");
  }, [aperto, carico, registro, sistemaSel, setSistemaSel]);

  if (registro) {
    return (
      <RegistroAperto registro={registro} sistemi={sistemi} puoScrivere={puoScrivere}
                      onIndietro={() => { setAperto(null); carica(); }} />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Sono i quaderni che le norme chiedono di tenere: oggi vivono come Word ed
        Excel sparsi sul disco, qui stanno insieme e le scadenze si vedono.
      </p>

      <div className="flex gap-2 flex-wrap items-center">
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
        <span className="text-xs text-slate-400 ml-auto">{elenco.length} registri</span>
      </div>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && elenco.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Nessun registro per questo sistema.
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {elenco.map((r) => (
          <Card key={r.codice}
                className={r.scadute ? "border-red-200 bg-red-50/50" : "border-slate-200"}>
            <CardContent className="p-4">
              <button type="button" onClick={() => setAperto(r.codice)}
                      data-testid={`apri-${r.codice}`}
                      className="w-full text-left space-y-2">
                <div className="flex items-start gap-2">
                  <BookOpen className="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{r.nome}</div>
                    <p className="text-xs text-slate-500 mt-0.5">{r.descrizione}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Tessere sistemi={sistemi} codici={r.sistemi} compatte />
                  {r.riservato && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">
                      <Shield className="w-2.5 h-2.5" /> riservato
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 text-xs">
                  <span className="text-slate-500">
                    <span className="font-semibold text-slate-800 tabular-nums">{r.righe}</span>
                    {" "}rig{r.righe === 1 ? "a" : "he"}
                  </span>
                  {r.scadute > 0 && (
                    <span className="text-red-600 font-semibold tabular-nums">
                      {r.scadute} scadut{r.scadute === 1 ? "a" : "e"}
                    </span>
                  )}
                  {r.in_scadenza > 0 && (
                    <span className="text-amber-700 font-semibold tabular-nums">
                      {r.in_scadenza} in scadenza
                    </span>
                  )}
                </div>
              </button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Come sta andando il registro: gli eventi per anno, quanto sono costati e
// come si ripartiscono. E' quello che serve mostrare in riesame, dove un
// elenco di duecento righe non dice niente a nessuno.
function AndamentoRegistro({ registro }) {
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [anno, setAnno] = useState("");
  // Una serie alla volta - un vettore energetico - dove il registro le ha:
  // i consumi si leggono cosi', e con tutto insieme si perde il filo.
  const [serie, setSerie] = useState("");
  const [apertoRaggr, setApertoRaggr] = useState(null);

  useEffect(() => {
    let vivo = true;
    setCarico(true);
    const p = new URLSearchParams();
    if (anno) p.set("anno", anno);
    if (serie) p.set("serie", serie);
    const q = p.toString();
    api.get(`/sgi/registri/${registro.codice}/andamento${q ? `?${q}` : ""}`)
      .then(({ data }) => { if (vivo) setDati(data); })
      .catch(() => { if (vivo) toast.error("Andamento non disponibile"); })
      .finally(() => { if (vivo) setCarico(false); });
    return () => { vivo = false; };
  }, [registro.codice, anno, serie]);

  if (carico) {
    return <Card className="border-slate-200"><CardContent className="p-6 text-center text-sm text-slate-500">
      Calcolo in corso...
    </CardContent></Card>;
  }
  if (!dati || (!dati.totale && !serie)) {
    return <Card className="border-dashed border-2"><CardContent className="p-6 text-center text-sm text-slate-500">
      Non ci sono ancora righe da cui ricavare un andamento.
    </CardContent></Card>;
  }

  const massimo = Math.max(1, ...dati.anni.map((a) => a.quante));
  const v = dati.variazione;
  const soldi = (n) => Number(n).toLocaleString("it-IT",
    { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
  // I chili si leggono male oltre le poche tonnellate: 14.090.900 kg non dice
  // niente a nessuno, 14.091 t si'.
  const misura = (col, n) => {
    if (col.tipo === "valuta") return soldi(n);
    if (/\(kg\)/i.test(col.nome) && Math.abs(n) >= 10000) {
      return `${(n / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })} t`;
    }
    return Number(n).toLocaleString("it-IT", { maximumFractionDigits: 2 });
  };
  const sommate = dati.colonne_sommate || [];

  return (
    <Card className="border-slate-200 bg-slate-50/60">
      <CardContent className="p-4 space-y-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h3 className="font-display text-lg font-black tracking-tight">Andamento</h3>
          <span className="text-xs text-slate-500">
            {dati.totale} righe in tutto
            {dati.senza_data > 0 && `, di cui ${dati.senza_data} senza data`}
          </span>
          {(dati.serie_disponibili || []).length > 0 && (
            <select value={serie} onChange={(e) => setSerie(e.target.value)}
                    data-testid="andamento-serie"
                    className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
              <option value="">{`Tutti — ${(dati.nome_serie || "serie").toLowerCase()}`}</option>
              {dati.serie_disponibili.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          {(dati.anni_disponibili || []).length > 1 && (
            <select value={anno} onChange={(e) => setAnno(e.target.value)}
                    data-testid="andamento-anno"
                    className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
              <option value="">Tutti gli anni</option>
              {dati.anni_disponibili.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          )}
          {v && !anno && !dati.serie_annuali && (
            <span className={`ml-auto text-xs font-semibold px-2 py-1 rounded-full border ${
              v.scarto > 0 ? "bg-red-50 text-red-700 border-red-200"
                : v.scarto < 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {v.anno}: {v.quante} contro {v.quante_prima} del {v.anno_prima}
              {v.scarto_pct !== null && ` (${v.scarto > 0 ? "+" : ""}${v.scarto_pct}%)`}
            </span>
          )}
        </div>

        {/* Dove si confronta una quantità — i consumi — le barre per anno
            contano le righe, cioè i mesi, e non dicono niente: dodici bollette
            contro nove. Qui invece, vettore per vettore, quanto si è consumato
            ogni anno, un anno accanto all'altro. */}
        {dati.serie_annuali ? (
          <div className="space-y-4">
            {dati.serie_annuali.map((s) => {
              const massimoSerie = Math.max(1, ...s.anni.map((a) => a.totale));
              const numero = (n) => Number(n).toLocaleString("it-IT", { maximumFractionDigits: 1 });
              const costoDi = (a) => {
                const c = registro.campi.find((x) => x.tipo === "valuta");
                return c && a.valori && a.valori[c.codice] ? soldi(a.valori[c.codice]) : "";
              };
              return (
                <div key={s.serie} className="bg-white rounded-lg border border-slate-200 p-3 space-y-1.5"
                     data-testid={"serie-" + s.serie}>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{s.serie}</span>
                    {s.unita && <span className="text-xs text-slate-400">{s.unita}</span>}
                    {s.unita_miste.length > 0 && (
                      <span className="text-[11px] text-amber-700">
                        attenzione: righe anche in {s.unita_miste.join(", ")}
                      </span>
                    )}
                    {s.variazione && (
                      <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        s.variazione.scarto > 0 ? "bg-red-50 text-red-700 border-red-200"
                          : s.variazione.scarto < 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                        {s.variazione.anno} sul {s.variazione.anno_prima}:{" "}
                        {s.variazione.scarto > 0 ? "+" : ""}{s.variazione.scarto_pct}%
                      </span>
                    )}
                  </div>
                  {s.anni.map((a) => {
                    const inCorso = a.anno === dati.anno_in_corso;
                    return (
                      <div key={a.anno} className="flex items-center gap-3">
                        <span className="text-xs tabular-nums text-slate-500 w-14 shrink-0">
                          {a.anno}{inCorso && <span className="text-[10px] text-slate-400"> in corso</span>}
                        </span>
                        <div className="flex-1 h-4 bg-slate-200/60 rounded-full overflow-hidden min-w-[60px]">
                          <div className={`h-full rounded-full ${inCorso ? "bg-[#0067b2]/40" : "bg-[#0067b2]"}`}
                               style={{ width: `${Math.round((a.totale / massimoSerie) * 100)}%` }} />
                        </div>
                        <span className="text-xs tabular-nums font-semibold w-24 text-right shrink-0">
                          {numero(a.totale)} {s.unita || ""}
                        </span>
                        <span className="text-[11px] tabular-nums text-slate-400 w-16 text-right shrink-0 hidden sm:block">
                          {a.quante} {a.quante === 1 ? "mese" : "mesi"}
                        </span>
                        <span className="text-xs tabular-nums text-slate-500 w-20 text-right shrink-0 hidden md:block">
                          {costoDi(a)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ) : (
        <div className="space-y-1.5">
          {dati.anni.map((a) => {
            const inCorso = a.anno === dati.anno_in_corso;
            const importi = registro.campi
              .filter((c) => c.tipo === "valuta")
              .map((c) => a.valori[c.codice])
              .filter((x) => x && x.totale > 0);
            return (
              <div key={a.anno} className="flex items-center gap-3">
                <span className="text-xs tabular-nums text-slate-500 w-14 shrink-0">
                  {a.anno}{inCorso && <span className="text-[10px] text-slate-400"> in corso</span>}
                </span>
                <div className="flex-1 h-5 bg-slate-200/60 rounded-full overflow-hidden min-w-[60px]">
                  <div className={`h-full rounded-full ${inCorso ? "bg-[#0067b2]/40" : "bg-[#0067b2]"}`}
                       style={{ width: `${Math.round((a.quante / massimo) * 100)}%` }} />
                </div>
                <span className="text-xs tabular-nums font-semibold w-10 text-right shrink-0">
                  {a.quante}
                </span>
                <span className="text-xs tabular-nums text-slate-500 w-24 text-right shrink-0 hidden sm:block">
                  {importi.length > 0 ? soldi(importi[0].totale) : ""}
                </span>
              </div>
            );
          })}
        </div>
        )}

        {dati.totali.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {dati.totali.filter((t) => t.quante > 0).map((t) => (
              <div key={t.codice} className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{t.nome}</div>
                <div className="text-lg font-black tabular-nums text-[#0067b2]">
                  {t.tipo === "valuta" ? soldi(t.totale)
                    : Number(t.totale).toLocaleString("it-IT")}
                </div>
                <div className="text-[11px] text-slate-400">
                  su {t.quante} {t.quante === 1 ? "riga valorizzata" : "righe valorizzate"}
                </div>
              </div>
            ))}
          </div>
        )}

        {dati.ripartizioni.filter((r) => r.voci.length > 0).map((r) => (
          <div key={r.codice} className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              {r.nome}
              {r.senza_valore > 0 && (
                <span className="normal-case tracking-normal text-slate-400">
                  {" "}· {r.senza_valore} da compilare
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.voci.map((voce) => (
                <span key={voce.valore}
                      className="text-xs bg-white border border-slate-200 rounded-full px-2.5 py-1">
                  {voce.valore}
                  <span className="ml-1.5 font-semibold tabular-nums text-[#0067b2]">
                    {voce.quante}
                  </span>
                </span>
              ))}
            </div>
          </div>
        ))}

        {(dati.raggruppamenti || []).filter((r) => r.voci.length > 0).map((r) => {
          const aperto = apertoRaggr === r.codice;
          const mostrate = aperto ? r.voci : r.voci.slice(0, 5);
          return (
            <div key={r.codice} className="space-y-1">
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                  {r.nome}
                </span>
                <span className="text-[11px] text-slate-400">
                  {r.distinti} distinti
                  {r.senza_valore > 0 && ` · ${r.senza_valore} senza`}
                </span>
                {r.voci.length > 5 && (
                  <button type="button" onClick={() => setApertoRaggr(aperto ? null : r.codice)}
                          className="ml-auto text-[11px] text-[#0067b2] font-medium">
                    {aperto ? "mostra i primi 5" : `vedi tutti (${r.voci.length})`}
                  </button>
                )}
              </div>
              <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {mostrate.map((voce) => (
                      <tr key={voce.valore} className="border-b border-slate-100 last:border-0">
                        <td className="p-2 max-w-[220px] truncate" title={voce.valore}>
                          {voce.valore}
                        </td>
                        <td className="p-2 text-right tabular-nums text-slate-500 w-16">
                          {voce.quante}
                        </td>
                        {sommate.map((col) => (
                          <td key={col.codice}
                              className="p-2 text-right tabular-nums whitespace-nowrap">
                            {voce.valori[col.codice]
                              ? misura(col, voce.valori[col.codice]) : ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {aperto && r.altri && (
                      <tr className="border-t border-slate-200 text-slate-500">
                        <td className="p-2 italic">altri {r.altri.quanti}</td>
                        <td className="p-2 text-right tabular-nums w-16">{r.altri.quante}</td>
                        {sommate.map((col) => (
                          <td key={col.codice}
                              className="p-2 text-right tabular-nums whitespace-nowrap">
                            {r.altri.valori[col.codice]
                              ? misura(col, r.altri.valori[col.codice]) : ""}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {dati.bandiere.filter((b) => b.si + b.no > 0).map((b) => (
          <div key={b.codice} className="text-xs text-slate-600">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">{b.nome}</span>
            {" "}· sì <span className="font-semibold tabular-nums">{b.si}</span>
            {" "}· no <span className="font-semibold tabular-nums">{b.no}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RegistroAperto({ registro, sistemi, puoScrivere, onIndietro }) {
  const annoCorrente = new Date().getFullYear();
  const [righe, setRighe] = useState([]);
  const [carico, setCarico] = useState(true);
  const [anno, setAnno] = useState("");
  const [cerca, setCerca] = useState("");
  const [modifica, setModifica] = useState(null);
  const [nuova, setNuova] = useState(false);
  const [importa, setImporta] = useState(false);
  const [compila, setCompila] = useState(false);
  const [vediAndamento, setVediAndamento] = useState(false);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (anno) p.set("anno", anno);
      if (cerca.trim()) p.set("cerca", cerca.trim());
      const { data } = await api.get(`/sgi/registri/${registro.codice}/voci?${p.toString()}`);
      setRighe(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally { setCarico(false); }
  }, [registro.codice, anno, cerca]);

  useEffect(() => {
    const t = setTimeout(carica, cerca ? 300 : 0);
    return () => clearTimeout(t);
  }, [carica, cerca]);

  // Lo stesso elenco che si vede - anno e ricerca compresi - in un Excel da
  // mandare a chi chiede i dati.
  const [scarico, setScarico] = useState(false);
  const scaricaExcel = async () => {
    setScarico(true);
    try {
      const p = new URLSearchParams();
      if (anno) p.set("anno", anno);
      if (cerca.trim()) p.set("cerca", cerca.trim());
      const { data } = await api.get(`/sgi/registri/${registro.codice}/excel?${p.toString()}`,
                                     { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${registro.nome}${anno ? " " + anno : ""}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella generazione del file"); }
    finally { setScarico(false); }
  };

  const elimina = async (r) => {
    if (!window.confirm("Eliminare questa riga?\n\nSe c'è un allegato viene cancellato con lei.")) return;
    try {
      await api.delete(`/sgi/registri/${registro.codice}/voci/${r.id}`);
      toast.success("Riga eliminata");
      carica();
    } catch { toast.error("Errore"); }
  };

  // Le colonne mostrate in elenco: le prime quattro che valgono la pena,
  // scelte fra data, titolo e i campi brevi.
  const colonne = useMemo(() => {
    const per = (cod) => registro.campi.find((c) => c.codice === cod);
    // Un registro puo' dire lui quali colonne contano: le letture dei
    // contatori mostrano il consumo calcolato, non le note.
    if (registro.colonne) return registro.colonne.map(per).filter(Boolean);
    const scelte = [];
    if (registro.campo_data) scelte.push(per(registro.campo_data));
    if (registro.campo_titolo && registro.campo_titolo !== registro.campo_data) {
      scelte.push(per(registro.campo_titolo));
    }
    for (const c of registro.campi) {
      if (scelte.length >= 5) break;
      if (scelte.some((x) => x && x.codice === c.codice)) continue;
      if (c.tipo === "testo_lungo") continue;
      scelte.push(c);
    }
    return scelte.filter(Boolean);
  }, [registro]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onIndietro}>
          <ArrowLeft className="w-3 h-3 mr-1" /> Registri
        </Button>
        <div className="flex-1 min-w-[220px]">
          <h2 className="font-display text-2xl font-black tracking-tight">{registro.nome}</h2>
          <p className="text-sm text-slate-500">{registro.descrizione}</p>
        </div>
        <Tessere sistemi={sistemi} codici={registro.sistemi} />
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={anno} onChange={(e) => setAnno(e.target.value)}>
          <option value="">Tutti gli anni</option>
          {[annoCorrente, annoCorrente - 1, annoCorrente - 2].map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca..."
                 className="h-9 border border-slate-200 rounded-full pl-8 pr-3 text-xs w-44" />
        </div>
        <Button size="sm" variant={vediAndamento ? "default" : "outline"}
                className={vediAndamento ? "bg-[#0067b2] hover:bg-[#005596] text-white" : ""}
                onClick={() => setVediAndamento((v) => !v)} data-testid="vedi-andamento">
          <TrendingUp className="w-3 h-3 mr-1" /> Andamento
        </Button>
        <Button size="sm" variant="outline" onClick={scaricaExcel} disabled={scarico}
                title="Le righe che vedi, con tutte le colonne, in Excel" data-testid="scarica-registro">
          {scarico ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 mr-1" />}
          Scarica Excel
        </Button>
        <span className="text-xs text-slate-400 ml-auto">{righe.length} righe</span>
        {puoScrivere && registro.compila && (
          <Button size="sm" variant="outline" onClick={() => setCompila(true)}
                  data-testid="compila-registro">
            <Gauge className="w-3 h-3 mr-1" /> {registro.compila_nome || "Compila"}
          </Button>
        )}
        {puoScrivere && registro.importa && (
          <Button size="sm" variant="outline" onClick={() => setImporta(true)}
                  data-testid="importa-registro">
            <Upload className="w-3 h-3 mr-1" /> Importa da Excel
          </Button>
        )}
        {puoScrivere && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuova(true)} data-testid="nuova-riga">
            <Plus className="w-3 h-3 mr-1" /> Nuova riga
          </Button>
        )}
      </div>

      {vediAndamento && <AndamentoRegistro registro={registro} />}

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && righe.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">
          Il registro è ancora vuoto.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {righe.map((r) => {
          const scad = registro.campo_scadenza ? r.dati[registro.campo_scadenza] : null;
          const inRitardo = scad && scad < new Date().toISOString().substring(0, 10);
          return (
            <Card key={r.id} className={inRitardo ? "border-red-200 bg-red-50/50" : "border-slate-200"}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px] grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                    {colonne.map((c) => (
                      <div key={c.codice} className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">{c.nome}</div>
                        <div className="text-sm truncate">
                          <ValoreCampo campo={c} riga={r} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {scad && (
                    <div className="min-w-[110px]">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">Riverifica</div>
                      <div className={`text-sm font-semibold tabular-nums ${inRitardo ? "text-red-600" : ""}`}>
                        {dataIt(scad)}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-1">
                    {r.file_url && (
                      <a href={fileUrl(r.file_url)} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" title={r.filename || "Allegato"}>
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </a>
                    )}
                    {puoScrivere && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setModifica(r)} title="Modifica">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => elimina(r)}
                                className="text-red-600" title="Elimina">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {(nuova || modifica) && (
        <DialogRiga registro={registro} riga={modifica}
                    onChiudi={() => { setNuova(false); setModifica(null); }}
                    onSalvato={() => { setNuova(false); setModifica(null); carica(); }} />
      )}
      {importa && (
        <DialogImportaRegistro registro={registro}
                               onChiudi={() => setImporta(false)}
                               onImportato={carica} />
      )}
      {compila && (
        <DialogCompilaDalTachigrafo registro={registro}
                                    onChiudi={() => setCompila(false)}
                                    onScritto={carica} />
      )}
    </div>
  );
}

// I numeri della "Raccolta dati autisti" li sa già il tachigrafo: si sceglie
// il periodo, si guarda cosa scriverebbe, e si scrive. Una riga per autista,
// con le note che dicono da dove viene ogni numero.
function DialogCompilaDalTachigrafo({ registro, onChiudi, onScritto }) {
  const oggi = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const ultimoQuadrimestre = () => {
    const q = Math.floor(oggi.getMonth() / 4);       // 0,1,2
    const anno = q === 0 ? oggi.getFullYear() - 1 : oggi.getFullYear();
    const mese = q === 0 ? 8 : (q - 1) * 4;          // inizio del quadrimestre precedente
    return [iso(new Date(anno, mese, 1)), iso(new Date(anno, mese + 4, 0))];
  };
  const [periodo, setPeriodo] = useState(ultimoQuadrimestre());
  const [aggiorna, setAggiorna] = useState(false);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);

  const manda = async (soloVerifica) => {
    setBusy(true);
    try {
      const { data } = await api.post(registro.compila, {
        dal: periodo[0], al: periodo[1], solo_verifica: soloVerifica, aggiorna,
      });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.scritte} righe scritte nel registro`);
        onScritto();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  const verificato = esito && esito.solo_verifica;
  const ore = (m) => `${Math.floor((m || 0) / 60)}:${String((m || 0) % 60).padStart(2, "0")}`;
  const STATO = {
    nuova: ["nuova", "bg-emerald-50 text-emerald-700"],
    aggiorna: ["si aggiorna", "bg-blue-50 text-blue-700"],
    gia_a_registro: ["già a registro", "bg-slate-100 text-slate-600"],
  };
  const presets = [
    ["Ultimo quadrimestre", ultimoQuadrimestre],
    ["Anno in corso", () => [`${oggi.getFullYear()}-01-01`, iso(oggi)]],
    ["Anno scorso", () => [`${oggi.getFullYear() - 1}-01-01`, `${oggi.getFullYear() - 1}-12-31`]],
  ];

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Compila dal tachigrafo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
            Una riga per ogni dipendente che ha guidato nel periodo: i chilometri dalle carte
            (o dai viaggi dell&apos;unità di bordo se la carta non è scaricata), le infrazioni
            — verbali CdS a suo nome nel registro sinistri, eccessi di velocità e giornate
            oltre i limiti registrati dal tachigrafo — e i sinistri stradali a suo nome. Nelle
            note il dettaglio.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            {presets.map(([n, f]) => (
              <Button key={n} size="sm" variant="outline" onClick={() => { setPeriodo(f()); setEsito(null); }}>{n}</Button>
            ))}
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Dal</Label>
              <Input type="date" value={periodo[0]} className="h-8"
                     onChange={(e) => { setPeriodo([e.target.value, periodo[1]]); setEsito(null); }} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Al</Label>
              <Input type="date" value={periodo[1]} className="h-8"
                     onChange={(e) => { setPeriodo([periodo[0], e.target.value]); setEsito(null); }} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={aggiorna}
                   onChange={(e) => { setAggiorna(e.target.checked); setEsito(null); }} />
            Riscrivi le righe già a registro per lo stesso autista e periodo
          </label>
          {esito && esito.righe.length === 0 && (
            <p className="text-sm text-amber-700">
              Nessun dato del tachigrafo nel periodo per dipendenti in elenco: carica prima i file .DDD
              (Mezzi e attrezzature &gt; Tachigrafo) e controlla gli abbinamenti.
            </p>
          )}
          {esito && esito.righe.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>{["Autista", "Km", "Giorni guida", "Ore guida", "Viaggi", "Infrazioni", "Sinistri", "Esito"].map((c) => (
                    <th key={c} className="text-left px-2 py-1">{c}</th>))}</tr>
                </thead>
                <tbody>
                  {esito.righe.map((r) => (
                    <tr key={r.employee_id} className="border-t border-slate-100">
                      <td className="px-2 py-1">{r.autista}{!r.da_carta && <span className="text-slate-400"> (carta non scaricata)</span>}</td>
                      <td className="px-2 py-1 tabular-nums">{r.km}</td>
                      <td className="px-2 py-1 tabular-nums">{r.giorni_guida}</td>
                      <td className="px-2 py-1 tabular-nums">{ore(r.guida_min)}</td>
                      <td className="px-2 py-1 tabular-nums">{r.viaggi}</td>
                      <td className={`px-2 py-1 tabular-nums ${r.infrazioni ? "text-red-700 font-semibold" : ""}`}>{r.infrazioni}</td>
                      <td className={`px-2 py-1 tabular-nums ${r.sinistri ? "text-red-700 font-semibold" : ""}`}>{r.sinistri}</td>
                      <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded ${STATO[r.stato]?.[1]}`}>{STATO[r.stato]?.[0] || r.stato}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {!verificato ? (
            <Button onClick={() => manda(true)} disabled={busy} data-testid="verifica-compila">
              {busy ? "Calcolo..." : "Verifica"}
            </Button>
          ) : (
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => manda(false)}
                    disabled={busy || !esito.da_scrivere} data-testid="conferma-compila">
              {busy ? "Scrivo..." : `Scrivi ${esito.da_scrivere} righe`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// L'elenco tenuto in Excel entra nel registro: prima si guarda cosa
// succederebbe, poi si applica. Persone e mezzi si abbinano all'anagrafica,
// e chi non si abbina non blocca la riga.
function DialogImportaRegistro({ registro, onChiudi, onImportato }) {
  const [file, setFile] = useState(null);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);

  const manda = async (soloVerifica) => {
    if (!file) { toast.error("Scegli il file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("solo_verifica", soloVerifica ? "true" : "false");
      const { data } = await api.post(registro.importa, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.importate} righe portate nel registro`);
        onImportato();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const verificato = esito && esito.solo_verifica;

  // Il modello da compilare: lo dà il server, con le tendine e le istruzioni.
  const scaricaModello = async () => {
    try {
      const { data, headers } = await api.get(registro.modello, { responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || `Modello_${registro.codice}.xlsx`;
      const u = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = u; a.download = nome; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nello scaricare il modello"); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Importa · {registro.nome}</DialogTitle></DialogHeader>

        {registro.codice === "consumi" ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-2">
            <p>
              <strong className="text-amber-800">Una riga per vettore e per mese.</strong>{" "}
              Scarica il modello, compilalo, ricaricalo: prima l&apos;app dice cosa
              succederebbe, poi confermi. Il mese si scrive come 2026-09, ma
              vanno bene anche 09/2026 e «settembre 2026».
            </p>
            <p>
              <strong className="text-amber-800">Niente entra due volte.</strong>{" "}
              Lo stesso vettore nello stesso mese entra una volta sola: se c&apos;è
              già a registro, la riga del file viene saltata e segnalata.
            </p>
          </div>
        ) : registro.codice === "rifiuti" || registro.codice === "inerti" ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-2">
            <p>
              <strong className="text-amber-800">Va bene l&apos;estrazione del gestionale.</strong>{" "}
              Il foglio con la colonna «Tipo Movimento» si riconosce da solo, e si
              divide: DTCSM finisce fra i rifiuti, DTCI e DTCMB fra gli inerti e
              le miscele. Si carica una volta e riempie tutti e due i registri.
            </p>
            <p>
              <strong className="text-amber-800">Niente entra due volte.</strong>{" "}
              Su quel foglio il numero del FIR non c&apos;è, quindi due righe sono
              la stessa riga quando combaciano documento, data, materiale,
              quantità e fornitore: lo stesso DDT con tre materiali resta tre
              righe, e lo stesso file ricaricato non raddoppia niente. Sul
              registro tenuto a mano invece comanda il numero del formulario, e
              regge le scritture diverse — «FIR 000123/24», «n. 123/24» e
              «0000123-24» sono lo stesso foglio.
            </p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <strong className="text-amber-800">Le righe già presenti non si duplicano.</strong>{" "}
            Il confronto è sulla data dell&apos;avvenimento e su cosa è successo. Persone e
            mezzi vengono abbinati all&apos;anagrafica; quelli che non si trovano restano
            scritti nelle note, così non si perde niente.
          </div>
        )}

        {registro.modello && (
          <Button variant="outline" size="sm" onClick={scaricaModello}
                  data-testid="scarica-modello">
            <Download className="w-3 h-3 mr-1" /> Scarica il modello Excel
          </Button>
        )}

        <div>
          <Label>File Excel</Label>
          <Input type="file" accept=".xlsx,.xlsm,.xls"
                 onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
        </div>

        {esito && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[["Nel file", esito.righe_nel_file],
                ["Da importare", esito.da_importare],
                ["Già presenti", esito.gia_presenti],
                ["Avvisi", (esito.avvisi || []).length]].map(([n, v]) => (
                <div key={n} className="border border-slate-200 rounded p-2">
                  <div className="font-display text-xl font-black tabular-nums">{v}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{n}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Dal {dataIt(esito.dal)} al {dataIt(esito.al)}.
            </p>

            {esito.per_registro && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="border border-slate-200 rounded p-2">
                  <div className="font-display font-bold tabular-nums">
                    {esito.per_registro.rifiuti} smaltimenti
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {(esito.chili / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })} t
                    {esito.viaggi > 0 && ` · ${esito.viaggi.toLocaleString("it-IT")} viaggi`}
                    {esito.costo_smaltimenti > 0
                      && ` · ${Math.round(esito.costo_smaltimenti).toLocaleString("it-IT")} €`}
                  </div>
                </div>
                <div className="border border-slate-200 rounded p-2">
                  <div className="font-display font-bold tabular-nums">
                    {esito.per_registro.inerti} acquisti
                  </div>
                  <div className="text-[11px] text-slate-500">
                    inerti e miscele
                    {esito.costo_acquisti > 0
                      && ` · ${Math.round(esito.costo_acquisti).toLocaleString("it-IT")} €`}
                  </div>
                </div>
              </div>
            )}

            {esito.per_cer && esito.per_cer.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {esito.per_cer.slice(0, 12).map((c) => (
                  <span key={c.cer} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {c.cer}: <span className="font-semibold tabular-nums">{c.righe}</span>
                  </span>
                ))}
                {esito.chili > 0 && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                    {esito.chili.toLocaleString("it-IT")} kg in tutto
                  </span>
                )}
              </div>
            )}

            {esito.per_vettore && esito.per_vettore.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {esito.per_vettore.map((v) => (
                  <span key={v.vettore} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {v.vettore}: <span className="font-semibold tabular-nums">{v.righe}</span>
                  </span>
                ))}
              </div>
            )}

            {(esito.scartate || []).length > 0 && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                <span className="font-semibold">{esito.scartate.length} righe</span>{" "}
                non si leggono e restano fuori:{" "}
                {esito.scartate.slice(0, 6).map((s) => `riga ${s.riga} (${s.motivo})`).join("; ")}
                {esito.scartate.length > 6 && "; …"}
              </div>
            )}

            {(esito.gia_a_registro || []).length > 0 && (
              <div className="text-xs text-slate-600">
                <span className="font-semibold">{esito.gia_a_registro.length} righe</span>{" "}
                sono già a registro e non rientrano:{" "}
                {esito.gia_a_registro.slice(0, 6).map(
                  (g) => `riga ${g.riga}${
                    g.formulario ? ` · FIR ${g.formulario}`
                    : g.vettore ? ` · ${g.vettore} ${meseIt(g.periodo)}`
                    : ` · ${g.data} ${g.cer}`}`
                ).join(" · ")}
                {esito.gia_a_registro.length > 6 && " …"}
              </div>
            )}
            {(esito.doppie_nel_file || []).length > 0 && (
              <div className="text-xs text-amber-700">
                <span className="font-semibold">{esito.doppie_nel_file.length} righe</span>{" "}
                sono ripetute dentro il file, e ne entra una sola:{" "}
                {esito.doppie_nel_file.slice(0, 6).map(
                  (d) => `riga ${d.riga}${d.prima_riga ? ` (come la ${d.prima_riga})` : ""}`
                ).join(" · ")}
                {esito.doppie_nel_file.length > 6 && " …"}
              </div>
            )}
            {(esito.scartate || []).length > 0 && (
              <div className="text-xs text-red-700">
                <span className="font-semibold">{esito.scartate.length} righe</span>{" "}
                non si possono leggere:{" "}
                {esito.scartate.slice(0, 6).map((s) => `riga ${s.riga} (${s.perche})`).join(" · ")}
                {esito.scartate.length > 6 && " …"}
              </div>
            )}
            {esito.senza_formulario > 0 && (
              <div className="text-xs text-slate-500">
                {esito.senza_formulario} righe entrano senza numero di formulario:
                su quelle il controllo dei doppioni si appoggia a data, CER e quantità.
              </div>
            )}

            {esito.per_tipologia && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(esito.per_tipologia).map(([t, n]) => (
                  <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {t}: <span className="font-semibold tabular-nums">{n}</span>
                  </span>
                ))}
              </div>
            )}

            {(esito.persone_non_trovate || []).length > 0 && (
              <div className="text-xs text-slate-600">
                <span className="font-semibold">{esito.persone_non_trovate.length} persone</span>{" "}
                non abbinate:{" "}
                {esito.persone_non_trovate.slice(0, 8).map(p => `${p.nome} (${p.righe})`).join(" · ")}
                {esito.persone_non_trovate.length > 8 && " …"}
              </div>
            )}
            {(esito.mezzi_non_trovati || []).length > 0 && (
              <div className="text-xs text-slate-600">
                <span className="font-semibold">{esito.mezzi_non_trovati.length} targhe</span>{" "}
                non in anagrafica:{" "}
                {esito.mezzi_non_trovati.slice(0, 8).map(m => `${m.targa} (${m.righe})`).join(" · ")}
                {esito.mezzi_non_trovati.length > 8 && " …"}
              </div>
            )}
            {(esito.avvisi || []).length > 0 && (
              <div className="text-xs text-amber-700">
                {esito.avvisi.slice(0, 4).map((a, i) => <div key={i}>{a}</div>)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          <Button variant="outline" onClick={() => manda(true)} disabled={busy || !file}>
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Verifica
          </Button>
          <Button onClick={() => manda(false)} disabled={busy || !file || !verificato}
                  className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MESI_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

/** «2026-09» come lo si dice: settembre 2026. */
function meseIt(v) {
  const [a, m] = String(v || "").split("-");
  const n = parseInt(m, 10);
  return a && n >= 1 && n <= 12 ? `${MESI_IT[n - 1]} ${a}` : String(v || "");
}

function ValoreCampo({ campo, riga }) {
  const v = riga.dati[campo.codice];
  if (v === null || v === undefined || v === "") return <span className="text-slate-300">—</span>;
  if (campo.tipo === "data") return <span className="tabular-nums">{dataIt(v)}</span>;
  if (campo.tipo === "mese") return <span>{meseIt(v)}</span>;
  if (campo.tipo === "si_no") {
    return v
      ? <span className="text-emerald-700 font-medium">sì</span>
      : <span className="text-slate-500">no</span>;
  }
  if (campo.tipo === "valuta") {
    return <span className="tabular-nums">{Number(v).toLocaleString("it-IT",
      { style: "currency", currency: "EUR" })}</span>;
  }
  if (campo.tipo === "numero") {
    return <span className="tabular-nums">{Number(v).toLocaleString("it-IT")}</span>;
  }
  if (campo.tipo === "persona" || campo.tipo === "mezzo") {
    return <span>{(riga.etichette || {})[campo.codice] || "—"}</span>;
  }
  return <span>{v}</span>;
}

function DialogRiga({ registro, riga, onChiudi, onSalvato }) {
  const modifica = Boolean(riga);
  const [valori, setValori] = useState(() => {
    const partenza = {};
    for (const c of registro.campi) {
      const v = riga?.dati?.[c.codice];
      partenza[c.codice] = v === null || v === undefined ? (c.tipo === "si_no" ? false : "") : v;
    }
    return partenza;
  });
  const [file, setFile] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const [persone, setPersone] = useState([]);
  const [mezzi, setMezzi] = useState([]);

  const servePersone = registro.campi.some((c) => c.tipo === "persona");
  const serveMezzi = registro.campi.some((c) => c.tipo === "mezzo");

  useEffect(() => {
    if (servePersone) {
      api.get("/employees").then(({ data }) => setPersone(data)).catch(() => {});
    }
    if (serveMezzi) {
      api.get("/equipment").then(({ data }) => setMezzi(data)).catch(() => {});
    }
  }, [servePersone, serveMezzi]);

  const cambia = (codice, v) => setValori((p) => ({ ...p, [codice]: v }));

  const salva = async () => {
    const mancante = registro.campi.find(
      (c) => c.obbligatorio && c.tipo !== "si_no"
             && (valori[c.codice] === "" || valori[c.codice] === null
                 || valori[c.codice] === undefined));
    if (mancante) { toast.error(`Manca il campo "${mancante.nome}"`); return; }
    setSalvo(true);
    try {
      if (modifica) {
        await api.put(`/sgi/registri/${registro.codice}/voci/${riga.id}`, valori);
        toast.success("Riga aggiornata");
      } else {
        const fd = new FormData();
        fd.append("dati", JSON.stringify(valori));
        if (file) fd.append("file", file);
        await api.post(`/sgi/registri/${registro.codice}/voci`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Riga aggiunta");
      }
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modifica ? "Modifica riga" : registro.nome}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {registro.campi.filter((c) => !c.calcolato).map((c) => (
            <div key={c.codice}>
              {c.tipo !== "si_no" && (
                <Label>{c.nome}{c.obbligatorio ? " *" : ""}</Label>
              )}
              <CampoRegistro campo={c} valore={valori[c.codice]}
                             onCambia={(v) => cambia(c.codice, v)}
                             persone={persone} mezzi={mezzi} />
              {c.nota && <p className="text-xs text-slate-500 mt-1">{c.nota}</p>}
            </div>
          ))}
          {registro.campi.some((c) => c.calcolato) && (
            <p className="text-xs text-slate-500">
              {registro.campi.filter((c) => c.calcolato).map((c) => c.nome).join(", ")}: li
              calcola il registro dalla lettura precedente dello stesso contatore.
            </p>
          )}
          {!modifica && (
            <div><Label>Allegato</Label>
              <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                     onChange={(e) => setFile(e.target.files[0])} />
              <p className="text-xs text-slate-500 mt-1">
                Facoltativo: il verbale, il formulario, la scheda firmata.
              </p>
            </div>
          )}
          {modifica && riga.filename && (
            <p className="text-xs text-slate-500">
              L&apos;allegato resta <span className="font-medium">{riga.filename}</span>.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : modifica ? "Salva" : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampoRegistro({ campo, valore, onCambia, persone, mezzi }) {
  const classe = "w-full h-10 border border-slate-200 rounded px-3";
  if (campo.tipo === "testo_lungo") {
    return (
      <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[64px]"
                value={valore} onChange={(e) => onCambia(e.target.value)} />
    );
  }
  if (campo.tipo === "si_no") {
    return (
      <label className="flex items-center gap-2 text-sm cursor-pointer py-1">
        <input type="checkbox" checked={Boolean(valore)}
               onChange={(e) => onCambia(e.target.checked)} />
        <span>{campo.nome}</span>
      </label>
    );
  }
  if (campo.tipo === "data") {
    return <Input type="date" value={valore || ""} onChange={(e) => onCambia(e.target.value)} />;
  }
  if (campo.tipo === "mese") {
    // Un mese intero: è il periodo dei registri che si compilano una volta al
    // mese, e chiedere due date di calendario per dire «settembre 2026» era
    // solo lento. Il selettore del browser dà già "AAAA-MM".
    return <Input type="month" value={(valore || "").substring(0, 7)}
                  onChange={(e) => onCambia(e.target.value)} />;
  }
  if (campo.tipo === "numero" || campo.tipo === "valuta") {
    return (
      <Input type="number" step={campo.tipo === "valuta" ? "0.01" : "any"}
             value={valore === null || valore === undefined ? "" : valore}
             onChange={(e) => onCambia(e.target.value)}
             placeholder={campo.tipo === "valuta" ? "€" : ""} />
    );
  }
  if (campo.tipo === "scelta") {
    return (
      <select className={classe} value={valore || ""} onChange={(e) => onCambia(e.target.value)}>
        <option value="">—</option>
        {(campo.opzioni || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (campo.tipo === "persona") {
    return (
      <select className={classe} value={valore || ""} onChange={(e) => onCambia(e.target.value)}>
        <option value="">—</option>
        {persone.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
      </select>
    );
  }
  if (campo.tipo === "mezzo") {
    return <SceltaMezzo mezzi={mezzi} valore={valore} onCambia={onCambia} />;
  }
  return <Input value={valore || ""} onChange={(e) => onCambia(e.target.value)} />;
}

// -----------------------------------------------------------------------------
// Libreria degli scenari di emergenza
// -----------------------------------------------------------------------------
function Emergenze({ puoScrivere }) {
  const [dati, setDati] = useState(null);
  const [carico, setCarico] = useState(true);
  const [nuovo, setNuovo] = useState(false);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const { data } = await api.get("/sgi/emergenze");
      setDati(data);
    } catch { toast.error("Errore nel caricamento degli scenari"); }
    finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const ritira = async (s) => {
    if (!window.confirm(
      `Ritirare lo scenario "${s.title}"?\n\n` +
      `Non verrà più proposto durante le verifiche in cantiere. ` +
      `Le prove già fatte restano nelle verifiche.`
    )) return;
    try {
      await api.delete(`/emergency-scenarios/${s.id}`);
      toast.success("Scenario ritirato");
      carica();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Sono le prove di emergenza che i preposti fanno durante le verifiche in cantiere.
        L&apos;app ne propone uno diverso ogni anno, a rotazione.
      </p>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!carico && dati && (
        <>
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-slate-400">
              {dati.scenari.length} scenari
              {dati.mai_provati > 0 && (
                <span className="text-amber-700 font-semibold"> · {dati.mai_provati} mai provati</span>
              )}
            </span>
            {puoScrivere && (
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white ml-auto"
                      onClick={() => setNuovo(true)} data-testid="nuovo-scenario">
                <Plus className="w-3 h-3 mr-1" /> Nuovo scenario
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {dati.scenari.map((s) => {
              const dellAnno = s.id === dati.scenario_anno;
              return (
                <Card key={s.id}
                      className={dellAnno ? "border-orange-300 bg-orange-50/50" : "border-slate-200"}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <Flame className={`w-4 h-4 mt-0.5 flex-shrink-0 ${dellAnno ? "text-orange-600" : "text-slate-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className="text-[10px]">{s.category}</Badge>
                          {dellAnno && (
                            <span className="text-[10px] font-semibold text-orange-700 bg-orange-100 border border-orange-200 rounded px-1.5 py-0.5">
                              scelto per il {dati.anno}
                            </span>
                          )}
                          {!s.usi && (
                            <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                              mai provato
                            </span>
                          )}
                        </div>
                        <div className="font-semibold text-sm leading-tight">{s.title}</div>
                      </div>
                    </div>

                    <p className="text-xs text-slate-600">{s.description}</p>

                    <div className="text-xs text-slate-500">
                      {(s.questions || []).length} domande
                      {s.usi > 0 && (
                        <> · provato {s.usi} volt{s.usi === 1 ? "a" : "e"}
                          {s.ultimo_uso ? `, l'ultima il ${dataIt(s.ultimo_uso)}` : ""}
                          {s.ultimo_cantiere ? ` a ${s.ultimo_cantiere}` : ""}</>
                      )}
                    </div>

                    {s.risposte_corrette !== null && s.risposte_corrette !== undefined && (
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-500">Risposte corrette</span>
                          <span className="font-semibold tabular-nums">{s.risposte_corrette}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full"
                               style={{ width: `${s.risposte_corrette}%`,
                                        background: s.risposte_corrette >= 75 ? "#059669"
                                                  : s.risposte_corrette >= 50 ? "#d97706" : "#dc2626" }} />
                        </div>
                      </div>
                    )}

                    {puoScrivere && (
                      <div className="flex justify-end pt-1">
                        <Button size="sm" variant="outline" onClick={() => ritira(s)}
                                className="text-red-600" title="Ritira dalla libreria">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {nuovo && (
        <DialogScenario categorie={dati?.categorie || []}
                        onChiudi={() => setNuovo(false)}
                        onSalvato={() => { setNuovo(false); carica(); }} />
      )}
    </div>
  );
}

function DialogScenario({ categorie, onChiudi, onSalvato }) {
  const [category, setCategory] = useState(categorie[0] || "Altro");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [domande, setDomande] = useState(["", "", ""]);
  const [salvo, setSalvo] = useState(false);

  const cambia = (i, v) => setDomande(domande.map((d, j) => (j === i ? v : d)));

  const salva = async () => {
    if (!title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (!description.trim()) { toast.error("Descrivi la situazione"); return; }
    const pulite = domande.map((d) => d.trim()).filter(Boolean);
    if (pulite.length === 0) { toast.error("Serve almeno una domanda"); return; }
    setSalvo(true);
    try {
      await api.post("/emergency-scenarios", {
        category, title: title.trim(), description: description.trim(), questions: pulite,
      });
      toast.success("Scenario aggiunto alla libreria");
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Nuovo scenario di emergenza</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div><Label>Categoria</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3" value={category}
                    onChange={(e) => setCategory(e.target.value)}>
              {categorie.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div><Label>Titolo *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="es. Sversamento di gasolio in cantiere" /></div>
          <div><Label>Che cosa succede *</Label>
            <textarea className="w-full border border-slate-200 rounded px-3 py-2 text-sm min-h-[70px]"
                      value={description} onChange={(e) => setDescription(e.target.value)}
                      placeholder="La situazione che viene raccontata alla squadra" /></div>
          <div>
            <Label>Domande alla squadra *</Label>
            <div className="space-y-2 mt-1">
              {domande.map((d, i) => (
                <Input key={i} value={d} onChange={(e) => cambia(i, e.target.value)}
                       placeholder={`Domanda ${i + 1}`} />
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-2"
                    onClick={() => setDomande([...domande, ""])}>
              <Plus className="w-3 h-3 mr-1" /> Aggiungi domanda
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Salvataggio..." : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Registro documenti di sistema
// -----------------------------------------------------------------------------
function Documenti({ sistemi, riferimenti, puoScrivere, sistemaSel, setSistemaSel,
                    onCambio, onCompila }) {
  const [righe, setRighe] = useState([]);
  const [carico, setCarico] = useState(true);
  const [tipo, setTipo] = useState("");
  const [stato, setStato] = useState("vigente");
  const [cerca, setCerca] = useState("");
  useDaCerca("cerca", setCerca);
  const [nuovo, setNuovo] = useState(false);
  const [importa, setImporta] = useState(false);
  const [revisioni, setRevisioni] = useState(null);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const p = new URLSearchParams();
      if (sistemaSel) p.set("sistema", sistemaSel);
      if (tipo) p.set("tipo", tipo);
      p.set("stato", stato);
      if (cerca.trim()) p.set("cerca", cerca.trim());
      const { data } = await api.get(`/sgi/documenti?${p.toString()}`);
      setRighe(data);
    } catch { toast.error("Errore nel caricamento dei documenti"); }
    finally { setCarico(false); }
  }, [sistemaSel, tipo, stato, cerca]);

  // Alcuni di questi documenti sono moduli che l'app compila: la due
  // diligence dell'assunzione, per dirne una. «Apri» da' il file originale,
  // che si legge e non si scrive: qui si segna quali hanno anche la versione
  // da riempire, perche' trovarla era una caccia al tesoro.
  const [compilabili, setCompilabili] = useState({});
  useEffect(() => {
    api.get("/sgi/schede")
      .then(({ data }) => {
        const mappa = {};
        for (const s of data.schede || []) {
          if (s.dove === "app" && s.codice) mappa[s.codice.toUpperCase()] = s.slug;
        }
        setCompilabili(mappa);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(carica, cerca ? 300 : 0);
    return () => clearTimeout(t);
  }, [carica, cerca]);

  const elimina = async (d) => {
    if (!window.confirm(
      `Eliminare "${d.titolo}"?\n\n` +
      `Vengono cancellati la scheda e il file allegato.\n` +
      `Se sostituiva una revisione precedente, quella torna in vigore.`
    )) return;
    try {
      await api.delete(`/sgi/documenti/${d.id}`);
      toast.success("Documento eliminato");
      carica(); onCambio();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap items-center">
        <FiltroSistemi sistemi={sistemi} valore={sistemaSel} onCambia={setSistemaSel} />
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {(riferimenti?.tipi_documento || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="h-9 border border-slate-200 rounded-full px-3 text-xs bg-white text-slate-600"
                value={stato} onChange={(e) => setStato(e.target.value)}>
          <option value="vigente">In vigore</option>
          <option value="bozza">Bozze</option>
          <option value="superato">Revisioni superate</option>
          <option value="tutti">Tutti</option>
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Codice o titolo..."
                 className="h-9 border border-slate-200 rounded-full pl-8 pr-3 text-xs w-48" />
        </div>
        <span className="text-xs text-slate-400 ml-auto">{righe.length} documenti</span>
        {puoScrivere && (
          <Button size="sm" variant="outline" onClick={() => setImporta(true)}
                  data-testid="importa-doc-sgi">
            <Upload className="w-3 h-3 mr-1" /> Carica più documenti
          </Button>
        )}
        {puoScrivere && (
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => setNuovo(true)} data-testid="carica-doc-sgi">
            <Plus className="w-3 h-3 mr-1" /> Carica documento
          </Button>
        )}
      </div>

      {carico && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}
      {!carico && righe.length === 0 && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500 space-y-2">
          <p>Nessun documento nel registro con questi filtri.</p>
          {stato === "vigente" && puoScrivere && (
            <p className="text-xs">
              Si comincia caricando il manuale e le procedure in vigore: il registro serve
              a sapere quale revisione è quella buona.
            </p>
          )}
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {righe.map((d) => (
          <Card key={d.id} className={d.stato === "superato" ? "border-slate-200 bg-slate-50" : "border-slate-200"}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start gap-2">
                <FileText className={`w-4 h-4 mt-0.5 flex-shrink-0 ${d.stato === "superato" ? "text-slate-400" : "text-orange-500"}`} />
                <div className="flex-1 min-w-0">
                  {d.codice && (
                    <div className="text-[10px] font-mono text-slate-400 truncate">{d.codice}</div>
                  )}
                  <div className="font-semibold text-sm leading-tight">{d.titolo}</div>
                </div>
              </div>

              <div className="flex gap-1.5 flex-wrap items-center">
                <Badge variant="outline" className="text-[10px]">{d.tipo}</Badge>
                {d.revisione && (
                  <Badge variant="outline" className="text-[10px]">rev. {d.revisione}</Badge>
                )}
                {d.stato === "superato" && (
                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-200 rounded px-1.5 py-0.5">
                    superato
                  </span>
                )}
                {d.stato === "bozza" && (
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                    bozza
                  </span>
                )}
                {d.riservato && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">
                    <Shield className="w-2.5 h-2.5" /> riservato
                  </span>
                )}
              </div>

              {d.data_revisione && (
                <div className="text-xs text-slate-500">Del {dataIt(d.data_revisione)}</div>
              )}

              <Tessere sistemi={sistemi} codici={d.sistemi} compatte />

              <div className="flex gap-1.5 flex-wrap pt-1">
                {d.file_url && (
                  <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm"><ExternalLink className="w-3 h-3 mr-1" />Apri</Button>
                  </a>
                )}
                {onCompila && d.codice && compilabili[d.codice.toUpperCase()] && (
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid={`compila-doc-${d.id}`}
                          onClick={() => onCompila(compilabili[d.codice.toUpperCase()])}>
                    <ClipboardCheck className="w-3 h-3 mr-1" /> Compila
                  </Button>
                )}
                {d.file_url && (
                  <BottoneRaccogli className="h-9 w-9 p-0"
                                   voce={{ tipo: "sgi", id: d.id, gruppo: "Sistemi di gestione",
                                           titolo: [d.codice, d.titolo].filter(Boolean).join(" · "),
                                           sottotitolo: [d.tipo, d.revisione ? `rev. ${d.revisione}` : null]
                                             .filter(Boolean).join(" · ") }} />
                )}
                <Button variant="outline" size="sm" onClick={() => setRevisioni(d)} title="Revisioni">
                  <History className="w-3 h-3" />
                </Button>
                {puoScrivere && (
                  <Button variant="outline" size="sm" onClick={() => elimina(d)}
                          className="text-red-600" title="Elimina">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {nuovo && (
        <DialogDocumento sistemi={sistemi} riferimenti={riferimenti}
                         onChiudi={() => setNuovo(false)}
                         onSalvato={() => { setNuovo(false); carica(); onCambio(); }} />
      )}
      {importa && (
        <DialogImportaDocumenti onChiudi={() => setImporta(false)}
                                onFatto={() => { setImporta(false); carica(); onCambio(); }} />
      )}
      {revisioni && <DialogRevisioni documento={revisioni} onChiudi={() => setRevisioni(null)} />}
    </div>
  );
}

/**
 * Carica in un colpo solo i documenti del sistema.
 *
 * Codice, titolo, revisione, data e norme stanno gia' dentro i file, nelle
 * proprieta' che alimentano il piede di pagina: si leggono da li' invece di
 * farle ribattere. Prima di scrivere mostra cosa entrerebbe, perche' venti
 * documenti alla volta non si controllano a occhio dopo.
 */
// Le evidenze degli anni passati - verbali di riunione, audit interni,
// riesami, righe di registro - entrano da un Excel con un foglio per genere.
// Prima si vede cosa entrerebbe, poi si scrive; niente entra due volte.
function DialogImportaEvidenze({ onChiudi, onFatto }) {
  const [file, setFile] = useState(null);
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);

  const manda = async (soloVerifica) => {
    if (!file) { toast.error("Scegli il file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("solo_verifica", soloVerifica ? "true" : "false");
      const { data } = await api.post("/sgi/evidenze/importa", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setEsito(data);
      if (!soloVerifica) {
        toast.success(`${data.scritte} evidenze registrate`);
        onFatto();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  const verificato = esito && esito.solo_verifica;
  const GENERE = { esecuzione: "scheda", audit: "audit", riesame: "riesame", registro: "registro" };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Importa evidenze da Excel</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
            Un Excel con un foglio per genere: <strong>Esecuzioni</strong> (scheda, data, esito, note),
            <strong> Audit</strong> (titolo, tipo, norme, data, auditor, ente, aree, esito, note),
            <strong> Riesami</strong> (anno, data, norme, partecipanti, valutazione, decisioni, note),
            <strong> Registri</strong> (registro e poi i campi del registro). Quello che c&apos;è già si salta.
          </p>
          <Input type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files[0]); setEsito(null); }} />
          {esito && (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap gap-2 text-xs">
                {Object.entries(esito.conteggi).map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {GENERE[k] || k}: <strong>{v}</strong>
                  </span>
                ))}
                {esito.saltate.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    già presenti: <strong>{esito.saltate.length}</strong>
                  </span>
                )}
                {esito.errori.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                    errori: <strong>{esito.errori.length}</strong>
                  </span>
                )}
              </div>
              {esito.errori.length > 0 && (
                <div className="border border-red-200 rounded divide-y divide-red-100 max-h-32 overflow-y-auto text-xs">
                  {esito.errori.map((e, i) => (
                    <div key={i} className="p-2"><span className="text-slate-500">{e.foglio}, riga {e.riga}:</span> {e.perche}</div>
                  ))}
                </div>
              )}
              {esito.da_scrivere.length > 0 && (
                <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-64 overflow-y-auto text-xs">
                  {esito.da_scrivere.map((r, i) => (
                    <div key={i} className="p-2 flex gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 w-16 flex-shrink-0">{GENERE[r.genere] || r.genere}</span>
                      <span>{r.cosa}</span>
                    </div>
                  ))}
                </div>
              )}
              {esito.saltate.length > 0 && (
                <div className="text-xs text-slate-500">
                  Già presenti, non si toccano: {esito.saltate.map((s) => s.cosa).join("; ")}.
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
          {!verificato ? (
            <Button onClick={() => manda(true)} disabled={busy || !file} data-testid="verifica-evidenze">
              {busy ? "Leggo..." : "Verifica"}
            </Button>
          ) : (
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => manda(false)}
                    disabled={busy || !esito.da_scrivere.length} data-testid="conferma-evidenze">
              {busy ? "Scrivo..." : `Registra ${esito.da_scrivere.length} evidenze`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogImportaDocumenti({ onChiudi, onFatto }) {
  const [esito, setEsito] = useState(null);
  const [busy, setBusy] = useState(false);
  const campo = React.useRef(null);
  const scelti = React.useRef([]);

  const manda = async (files, soloVerifica) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    fd.append("solo_verifica", soloVerifica ? "true" : "false");
    const { data } = await api.post("/sgi/documenti/importa", fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    return data;
  };

  const scegli = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      scelti.current = files;
      setEsito(await manda(files, true));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella lettura dei file");
    } finally { setBusy(false); }
  };

  const conferma = async () => {
    setBusy(true);
    try {
      const v = await manda(scelti.current, false);
      toast.success(`${v.caricati} documenti nel registro`);
      onFatto();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally { setBusy(false); }
  };

  const righe = esito ? [...esito.nuovi, ...esito.revisioni] : [];

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Carica più documenti</DialogTitle></DialogHeader>

        <input ref={campo} type="file" multiple className="hidden"
               accept=".docx,.xlsx,.pptx,.pdf,.doc,.xls,.zip" onChange={scegli} />

        {!esito ? (
          <div className="space-y-3">
            <p className="text-sm">
              Seleziona i documenti tutti insieme, oppure un unico ZIP. Codice, titolo,
              revisione, data e norme coperte si leggono da dentro i file Word nuovi; per
              PDF e Word vecchi si mette insieme a loro un <strong>foglio di
              accompagnamento</strong> Excel con una riga per file (file, codice, titolo,
              tipo, revisione, data, norme).
            </p>
            <p className="text-xs text-slate-500">
              Se un documento ha già una revisione nel registro, quella passa a
              &laquo;superata&raquo; e resta consultabile nello storico. I file senza le
              proprietà e senza una riga nel foglio si caricano uno per uno con
              &laquo;Carica documento&raquo;.
            </p>
            <Button onClick={() => campo.current?.click()} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="scegli-documenti">
              <Upload className="w-4 h-4 mr-2" />
              {busy ? "Leggo..." : "Scegli i documenti"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">Ecco cosa entrerebbe. Niente è ancora stato salvato.</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Nuovi", esito.nuovi.length, "text-emerald-700 bg-emerald-50 border-emerald-200"],
                ["Nuove revisioni", esito.revisioni.length, "text-sky-700 bg-sky-50 border-sky-200"],
                ["Scartati", esito.scartati.length, "text-amber-800 bg-amber-50 border-amber-200"]]
                .map(([nome, quanti, classe]) => (
                  <div key={nome} className={`border rounded p-2 ${classe}`}>
                    <div className="text-xl font-black tabular-nums">{quanti}</div>
                    <div className="text-[10px] uppercase tracking-wider">{nome}</div>
                  </div>
                ))}
            </div>

            {esito.dal_foglio > 0 && (
              <p className="text-xs text-slate-500">{esito.dal_foglio} documenti descritti dal foglio di accompagnamento.</p>
            )}
            {esito.senza_file?.length > 0 && (
              <p className="text-xs text-amber-800">
                Nel foglio ma senza il file: {esito.senza_file.map((s) => s.file).join(", ")}.
              </p>
            )}
            {esito.scartati.length > 0 && (
              <div className="border border-amber-200 rounded divide-y divide-amber-100 max-h-36 overflow-y-auto">
                {esito.scartati.map((s, i) => (
                  <div key={i} className="p-2 text-xs">
                    <span className="font-semibold">{s.file}</span>
                    <span className="text-amber-800"> — {s.perche}</span>
                  </div>
                ))}
              </div>
            )}

            {righe.length > 0 && (
              <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {righe.map((d, i) => (
                  <div key={i} className="p-2 text-xs flex items-center gap-2 flex-wrap">
                    <span className="font-semibold w-20 flex-shrink-0">{d.codice}</span>
                    <span className="flex-1 min-w-[140px] truncate" title={d.titolo}>
                      {d.titolo}
                    </span>
                    <span className="text-slate-500 flex-shrink-0">rev {d.revisione}</span>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">{d.tipo}</span>
                    {d.sostituisce && (
                      <span className="text-[10px] bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2">
                        sostituisce la rev {d.sostituisce.revisione}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => (esito ? setEsito(null) : onChiudi())}>
            {esito ? "Indietro" : "Annulla"}
          </Button>
          {esito && righe.length > 0 && (
            <Button onClick={conferma} disabled={busy}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="conferma-import-doc">
              {busy ? "Carico..." : `Carica ${righe.length} documenti`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogDocumento({ sistemi, riferimenti, onChiudi, onSalvato }) {
  const [codice, setCodice] = useState("");
  const [titolo, setTitolo] = useState("");
  const [tipo, setTipo] = useState("Procedura");
  const [revisione, setRevisione] = useState("");
  const [dataRevisione, setDataRevisione] = useState("");
  const [scelti, setScelti] = useState([]);
  const [riservato, setRiservato] = useState(false);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [salvo, setSalvo] = useState(false);

  const salva = async () => {
    if (!titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (scelti.length === 0) { toast.error("Indica almeno un sistema"); return; }
    setSalvo(true);
    try {
      const fd = new FormData();
      if (codice.trim()) fd.append("codice", codice.trim());
      fd.append("titolo", titolo.trim());
      fd.append("tipo", tipo);
      if (revisione.trim()) fd.append("revisione", revisione.trim());
      if (dataRevisione) fd.append("data_revisione", dataRevisione);
      fd.append("sistemi", scelti.join(","));
      fd.append("riservato", riservato ? "true" : "false");
      if (note) fd.append("note", note);
      if (file) fd.append("file", file);
      const { data } = await api.post("/sgi/documenti", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (data.superato) {
        toast.success(`Caricato. La revisione ${data.superato.revisione || "precedente"} è passata fra le superate.`);
      } else {
        toast.success("Documento registrato");
      }
      onSalvato();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSalvo(false); }
  };

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Carica un documento di sistema</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Codice</Label>
              <Input value={codice} onChange={(e) => setCodice(e.target.value)}
                     placeholder="es. PR 4.5.5.1" /></div>
            <div><Label>Revisione</Label>
              <Input value={revisione} onChange={(e) => setRevisione(e.target.value)} placeholder="es. 4" /></div>
          </div>
          <p className="text-xs text-slate-500 -mt-1">
            Con lo stesso codice, la revisione precedente passa da sola fra le superate.
          </p>
          <div><Label>Titolo *</Label>
            <Input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                   placeholder="es. Audit Interni" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tipo</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={tipo}
                      onChange={(e) => setTipo(e.target.value)}>
                {(riferimenti?.tipi_documento || []).map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><Label>Data della revisione</Label>
              <Input type="date" value={dataRevisione} onChange={(e) => setDataRevisione(e.target.value)} /></div>
          </div>
          <div><Label>Sistemi a cui appartiene *</Label>
            <div className="mt-1"><SceltaSistemi sistemi={sistemi} scelti={scelti} onCambia={setScelti} /></div>
          </div>
          <div><Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div><Label>File</Label>
            <Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                   onChange={(e) => setFile(e.target.files[0])} />
            <p className="text-xs text-slate-500 mt-1">
              Facoltativo: puoi registrare il documento anche solo per tenerne traccia,
              lasciando il file dov&apos;è.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={riservato} onChange={(e) => setRiservato(e.target.checked)} />
            <span>Riservato — visibile solo a direzione e amministrazione</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={salva} disabled={salvo} className="bg-orange-500 hover:bg-orange-600 text-white">
            {salvo ? "Caricamento..." : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogRevisioni({ documento, onChiudi }) {
  const [righe, setRighe] = useState(null);
  useEffect(() => {
    api.get(`/sgi/documenti/${documento.id}/revisioni`)
      .then(({ data }) => setRighe(data))
      .catch(() => setRighe([]));
  }, [documento.id]);

  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revisioni · {documento.codice || documento.titolo}</DialogTitle>
        </DialogHeader>
        {righe === null && <p className="text-sm text-slate-500 py-4">Caricamento...</p>}
        {righe && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {righe.map((d) => (
              <div key={d.id}
                   className={`border rounded p-3 ${d.stato === "vigente" ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200"}`}>
                <div className="flex justify-between gap-2 items-center flex-wrap">
                  <span className="text-sm font-semibold">
                    {d.revisione ? `Revisione ${d.revisione}` : "Senza numero di revisione"}
                  </span>
                  <span className="text-xs text-slate-500">{dataIt(d.data_revisione)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {d.stato === "vigente"
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" /> in vigore
                      </span>
                    : <span className="text-[10px] font-semibold text-slate-500">superata</span>}
                  {d.file_url && (
                    <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-[#0067b2] font-medium inline-flex items-center gap-1">
                      apri <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onChiudi}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
